<template>
    <PageContainer>
        <MainContent>
            <RightSection>
                <TopSection>
                    <!-- ! 청구 자동화 섹션 -->
                    <LeftAutomationSection>
                        <ClaimsCard>
                            <AutomationHeader>
                                <AutomationTitleContainer>
                                    <ClaimsTitle>청구 자동화</ClaimsTitle>
                                    <ClaimListButton type="submit" @click="openLongtermLoginModal">
                                        <Monitor :size="20" :style="{ paddingTop: '5px' }" />
                                        공단 로그인창
                                    </ClaimListButton>
                                    <!-- 환자명 검색 입력 필드 -->
                                    <SearchContainer>
                                        <SearchIconWrapper>
                                            <Magnify :size="18" :style="{ paddingTop: '3px' }" />
                                        </SearchIconWrapper>
                                        <SearchInput v-model="searchQuery" type="text" placeholder="환자명 검색..."
                                            @input="handleSearchInput" />
                                        <ClearSearchButton v-if="searchQuery" @click="clearSearch" title="검색어 지우기">
                                            ×
                                        </ClearSearchButton>
                                    </SearchContainer>
                                    <!-- <ClaimListButton @click="showSettingsModal = true">
                                        <Cog :size="20" :style="{ paddingTop: '3px' }" />
                                        설정 관리
                                    </ClaimListButton> -->
                                </AutomationTitleContainer>
                                <AutomationActions>
                                    <AutomationStatusBadge :class="{
                                        active: !isAutomationActive,
                                        stopping: isAutomationActive
                                    }" @click="toggleOverallAutomation">
                                        <StatusIcon>{{ isAutomationActive ? '🔴' : '🟢' }}</StatusIcon>
                                        {{ getAutomationButtonText }}
                                    </AutomationStatusBadge>
                                </AutomationActions>
                            </AutomationHeader>

                            <AutomationContent>
                                <OxygenAutomationSection>
                                    <StatsCard>
                                        <StatsGrid>
                                            <!-- * 전체 카드 -->
                                            <StatsItem :class="{
                                                'processing-item': false,
                                                'selected': oxygenFilter === 'all'
                                            }" @click="handleOxygenStatsClick('all')" style="cursor: pointer;">
                                                <StatsValue>{{ oxygenPatients.length }}</StatsValue>
                                                <StatsLabel>전체</StatsLabel>
                                            </StatsItem>
                                            <StatsItem :class="{
                                                'processing-item': false,
                                                'selected': oxygenFilter === 'pending'
                                            }" @click="handleOxygenStatsClick('pending')" style="cursor: pointer;">
                                                <StatsValue>{{ getOxygenPendingCount() }}</StatsValue>
                                                <StatsLabel>청구 대기</StatsLabel>
                                            </StatsItem>
                                            <!-- * 진행중 카드 -->
                                            <StatsItem :class="{ 'processing-item': true }">
                                                <StatsValue>
                                                    <ProcessingPatient v-if="currentProcessingPatient">
                                                        {{ currentProcessingPatient.name }}
                                                    </ProcessingPatient>
                                                    <span v-else>-</span>
                                                </StatsValue>
                                                <StatsLabel>진행 중</StatsLabel>
                                            </StatsItem>
                                            <!-- * 완료 카드 -->
                                            <StatsItem :class="{
                                                'processing-item': false,
                                                'selected': oxygenFilter === 'completed'
                                            }" @click="handleOxygenStatsClick('completed')" style="cursor: pointer;">
                                                <StatsValue>{{ getOxygenCompletedCount() }}</StatsValue>
                                                <StatsLabel>완료</StatsLabel>
                                            </StatsItem>
                                            <!-- * 실패 카드 -->
                                            <StatsItem :class="{
                                                'processing-item': false,
                                                'selected': oxygenFilter === 'failed'
                                            }" @click="handleOxygenStatsClick('failed')" style="cursor: pointer;">
                                                <StatsValue>{{ automationFailures.length }}</StatsValue>
                                                <StatsLabel>실패</StatsLabel>
                                            </StatsItem>
                                        </StatsGrid>
                                    </StatsCard>

                                    <!-- * 환자 목록 테이블 -->
                                    <ClaimsTable v-if="filteredOxygenPatients.length > 0">
                                        <ClaimsTableHeader>
                                            <ClaimsTableRow>
                                                <AutomationTableHeaderCell>환자명</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>주민등록번호</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>연락처</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>처방전 발행일</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>처방기간</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>산소치료 사용기간</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>청구차수</AutomationTableHeaderCell>
                                                <AutomationTableHeaderCell>청구진행상태</AutomationTableHeaderCell>
                                            </ClaimsTableRow>
                                        </ClaimsTableHeader>
                                        <ClaimsTableBody>
                                            <ClaimsTableRow v-for="patient in filteredOxygenPatients" :key="patient.id"
                                                :class="{
                                                    active: selectedOxygenPatient?.id === patient.id,
                                                    'failed-row': oxygenFilter === 'failed' &&
                                                        automationFailures.some(failure => failure.patient_id === patient.id)
                                                }" @click="openOrderModifyModal(patient, 'oxygen')">
                                                <AutomationTableCell>
                                                    <PatientName>{{ patient.name }}</PatientName>
                                                </AutomationTableCell>
                                                <AutomationTableCell>{{ formatPatientNumber(patient.number) }}
                                                </AutomationTableCell>
                                                <AutomationTableCell>{{ formatPhoneNumber(patient.phone) || '-' }}
                                                </AutomationTableCell>
                                                <AutomationTableCell>
                                                    <DateDisplay>{{ formatDate(patient.oxygenDate) || '없음' }}
                                                    </DateDisplay>
                                                </AutomationTableCell>
                                                <AutomationTableCell>
                                                    <ValidityPeriodContainer>
                                                        <ValidityInputGroup>
                                                            <ValidityLabel>시작일:</ValidityLabel>
                                                            <ValidityDateValue>{{ formatDate(patient.startDate) || '-'
                                                            }}</ValidityDateValue>
                                                        </ValidityInputGroup>
                                                        <ValidityInputGroup>
                                                            <ValidityLabel>종료일:</ValidityLabel>
                                                            <ValidityDateValue>{{ formatDate(patient.endDate) || '-' }}
                                                            </ValidityDateValue>
                                                        </ValidityInputGroup>
                                                    </ValidityPeriodContainer>
                                                </AutomationTableCell>
                                                <AutomationTableCell>
                                                    <ValidityPeriodContainer>
                                                        <ValidityInputGroup
                                                            v-if="patient.receiptType === '가정용' || patient.receiptType === '동시접수'">
                                                            <ValidityLabel>가정용:</ValidityLabel>
                                                            <ValidityDateValue>{{ formatDate(patient.homeStartDate) ||
                                                                '-' }} ~ {{ formatDate(patient.homeEndDate) || '-' }}
                                                            </ValidityDateValue>
                                                        </ValidityInputGroup>
                                                        <ValidityInputGroup
                                                            v-if="patient.receiptType === '휴대용' || patient.receiptType === '동시접수'">
                                                            <ValidityLabel>휴대용:</ValidityLabel>
                                                            <ValidityDateValue>{{ formatDate(patient.mobileStartDate) ||
                                                                '-' }} ~ {{ formatDate(patient.mobileEndDate) || '-' }}
                                                            </ValidityDateValue>
                                                        </ValidityInputGroup>
                                                        <ValidityInputGroup
                                                            v-if="!patient.receiptType || patient.receiptType === ''">
                                                            <ValidityLabel>사용기간:</ValidityLabel>
                                                            <ValidityDateValue>{{ formatDate(patient.startDate) || '-'
                                                            }} ~ {{ formatDate(patient.endDate) || '-' }}
                                                            </ValidityDateValue>
                                                        </ValidityInputGroup>
                                                    </ValidityPeriodContainer>
                                                </AutomationTableCell>
                                                <AutomationTableCell>
                                                    {{ getClaimCountText(patient) }}
                                                </AutomationTableCell>
                                                <AutomationTableCell>
                                                    <ClaimStatusBadge :class="getClaimStatusClass(patient.status)">
                                                        {{ getClaimStatusText(patient.status) }}
                                                    </ClaimStatusBadge>
                                                </AutomationTableCell>
                                            </ClaimsTableRow>
                                        </ClaimsTableBody>

                                        <!-- * 실패 상세 정보 섹션 -->
                                        <ClaimsTableFooter
                                            v-if="oxygenFilter === 'failed' && filteredOxygenPatients.length > 0">
                                            <ClaimsTableRow>
                                                <AutomationTableCell colspan="8">
                                                    <FailedErrorsSection>
                                                        <!-- 헤더 섹션 -->
                                                        <FailedLogHeader>
                                                            <FailedLogTitle>실패 로그 ({{ automationFailures.length }}건)
                                                            </FailedLogTitle>
                                                            <FailedLogActions>
                                                                <ClearAllButton @click="clearAllFailureLogs">
                                                                    전체 삭제
                                                                </ClearAllButton>
                                                            </FailedLogActions>
                                                        </FailedLogHeader>

                                                        <FailedErrorsList>
                                                            <!-- 저장된 실패 로그들만 표시 -->
                                                            <FailedErrorItem v-for="failure in automationFailures"
                                                                :key="`stored-${failure.num}`">
                                                                <FailedErrorContent>
                                                                    <FailedLogInfo>
                                                                        <FailedErrorMessage>
                                                                            <strong>{{ failure.patient_name }}</strong>
                                                                            - {{ failure.failure_step }} 실패
                                                                            <br>{{ failure.failure_reason }}
                                                                        </FailedErrorMessage>
                                                                        <FailedTime>{{ formatDate(failure.created_at) }}
                                                                        </FailedTime>
                                                                    </FailedLogInfo>
                                                                    <FailedLogActions>
                                                                        <ViewOrderButton
                                                                            @click="openOrderModifyModal(getFailedPatientOrderInfo(failure.patient_id), 'oxygen')">
                                                                            주문서 보기
                                                                        </ViewOrderButton>
                                                                        <DeleteLogButton
                                                                            @click="deleteFailureLog(failure.num)">
                                                                            삭제
                                                                        </DeleteLogButton>
                                                                    </FailedLogActions>
                                                                </FailedErrorContent>
                                                            </FailedErrorItem>
                                                        </FailedErrorsList>
                                                    </FailedErrorsSection>
                                                </AutomationTableCell>
                                            </ClaimsTableRow>
                                        </ClaimsTableFooter>
                                    </ClaimsTable>

                                    <EmptyState v-else>
                                        <EmptyIcon>👥</EmptyIcon>
                                        <EmptyTitle>선택한 조건에 맞는 환자가 없습니다</EmptyTitle>
                                        <EmptyDescription>다른 필터를 선택해보세요</EmptyDescription>
                                    </EmptyState>
                                </OxygenAutomationSection>
                            </AutomationContent>
                        </ClaimsCard>
                    </LeftAutomationSection>

                    <!-- ! 청구 일정 섹션 -->
                    <RightScheduleSection>
                        <ClaimsCard>
                            <!-- * 청구 일정 헤더 -->
                            <ClaimsHeader>
                                <ClaimsTitle>
                                    청구 일정
                                    <span class="schedule-count">(총 {{ upcomingClaims.length }}건)</span>
                                </ClaimsTitle>
                                <ClaimsActions>
                                    <FilterSelect v-model="scheduleFilter" @change="handleScheduleFilterChange">
                                        <option value="all">전체</option>
                                        <option value="oxygen">산소발생기</option>
                                        <option value="diabetes">당뇨소모성재료</option>
                                        <option value="expired">처방전 만료일</option>
                                    </FilterSelect>
                                </ClaimsActions>
                            </ClaimsHeader>

                            <!-- * 달력 섹션 -->
                            <CalendarSection>
                                <CalendarHeader>
                                    <CalendarNavigation>
                                        <CalendarButton @click="previousMonth">
                                            <span>‹</span>
                                        </CalendarButton>
                                        <CalendarTitle>{{ currentMonthYear }}</CalendarTitle>
                                        <CalendarButton @click="nextMonth">
                                            <span>›</span>
                                        </CalendarButton>
                                    </CalendarNavigation>

                                    <!-- 색상 범례 추가 -->
                                    <CalendarLegend>
                                        <LegendItem>
                                            <LegendColor class="oxygen" />
                                            <span>산소</span>
                                        </LegendItem>
                                        <LegendItem>
                                            <LegendColor class="diabetes" />
                                            <span>당뇨</span>
                                        </LegendItem>
                                        <LegendItem>
                                            <LegendColor class="expired" />
                                            <span>만료</span>
                                        </LegendItem>
                                    </CalendarLegend>
                                </CalendarHeader>

                                <CalendarGrid>
                                    <CalendarDayHeader v-for="day in ['일', '월', '화', '수', '목', '금', '토']" :key="day"
                                        :class="{
                                            sunday: day === '일',
                                            saturday: day === '토'
                                        }">
                                        {{ day }}
                                    </CalendarDayHeader>
                                    <CalendarDay v-for="day in calendarDays" :key="day.date" :class="getDayClass(day)"
                                        @click="selectDay(day, $event)">
                                        <CalendarDayNumber>{{ day.day }}</CalendarDayNumber>
                                        <CalendarDayEvents v-if="day.events && day.events.length > 0">
                                            <CalendarEvent v-for="event in day.events.slice(0, 2)" :key="event.id"
                                                :class="[event.type, { 'completed': event.isCompleted }]">
                                                {{ event.title }}
                                                <span v-if="event.isCompleted"
                                                    style="font-weight: bold; margin-left: 4px;">✓</span>
                                            </CalendarEvent>
                                            <CalendarMoreEvents v-if="day.events.length > 2">
                                                +{{ day.events.length - 2 }}
                                            </CalendarMoreEvents>
                                        </CalendarDayEvents>

                                        <CalendarTooltip
                                            v-if="tooltipDay && tooltipDay.date === day.date && day.events && day.events.length > 0"
                                            :style="{ top: tooltipPosition.y + 'px', left: tooltipPosition.x + 'px' }">
                                            <TooltipTitle>{{ formatSelectedDate(day.date) }}</TooltipTitle>
                                            <TooltipEventList>
                                                <TooltipEventItem v-for="event in day.events" :key="event.id"
                                                    :class="event.type">
                                                    <TooltipEventTitle>
                                                        {{ event.title }}
                                                        <span v-if="event.isCompleted"
                                                            style="color: #4caf50; font-weight: bold; margin-left: 4px;">✓</span>
                                                    </TooltipEventTitle>
                                                    <TooltipEventDesc>{{ event.description }}</TooltipEventDesc>
                                                </TooltipEventItem>
                                            </TooltipEventList>
                                        </CalendarTooltip>
                                    </CalendarDay>
                                </CalendarGrid>
                            </CalendarSection>
                        </ClaimsCard>
                    </RightScheduleSection>
                </TopSection>

                <!-- ! 청구 내역 섹션 -->
                <ClaimsCard>
                    <ClaimsHeader>
                        <ClaimsTitle>
                            청구 내역
                            <span v-if="currentClaimTab === 'claim-list'">
                                ({{ filteredCrawledClaims2.length }}건 / 전체 {{ totalCount }}건)
                            </span>
                            <span v-else-if="currentClaimTab === 'assistance'">
                                ({{ filteredCrawledClaims3.length }}건 / 전체 {{ totalCount }}건)
                            </span>
                            <span v-else>
                                ({{ filteredCrawledClaims.length }}건 / 전체 {{ totalCount }}건)
                            </span>
                            <ClaimListButton @click="loadSavedClaims">
                                <History :size="20" :style="{ paddingTop: '3px' }" />
                                최근 청구내역 불러오기
                            </ClaimListButton>
                        </ClaimsTitle>
                        <ClaimsActions>
                            <FilterSelect v-model="claimsFilter" @change="handleFilterChange">
                                <option value="all">전체</option>
                                <option value="pending">대기</option>
                                <option value="approved">승인</option>
                                <option value="rejected">반려</option>
                            </FilterSelect>

                            <DateSelectionGroup>
                                <DateInput type="date" v-model="claimDateRange.startDate"
                                    :max="claimDateRange.endDate || today" @change="handleStartDateChange" />
                                <DateSeparator>~</DateSeparator>
                                <DateInput type="date" v-model="claimDateRange.endDate" :min="claimDateRange.startDate"
                                    :max="today" @change="handleEndDateChange" />
                            </DateSelectionGroup>

                            <AddClaimButton @click="checkClaim">
                                <TextSearch :size="20" :style="{ paddingTop: '3px' }" />
                                공단 청구내역 조회
                            </AddClaimButton>
                        </ClaimsActions>
                    </ClaimsHeader>

                    <!-- * 청구 내역 탭 -->
                    <ClaimsTabs>
                        <TabButton v-for="tab in claimTabs" :key="tab.value"
                            :class="{ active: currentClaimTab === tab.value }" @click="currentClaimTab = tab.value">
                            {{ tab.label }}
                        </TabButton>
                    </ClaimsTabs>

                    <!-- * 청구위임내역 테이블 -->
                    <ClaimsTable v-if="currentClaimTab === 'claim-list' && filteredCrawledClaims2.length > 0"
                        class="delegation-table">
                        <ClaimsTableHeader>
                            <ClaimsTableRow>
                                <ClaimsTableHeaderCell>처리상태</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>처리지사</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>접수일자</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>수진자주민번호</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>수진자명</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>요양비종류</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>위임동의기간</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>보류(반려)사유</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>금융정보</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>연락처</ClaimsTableHeaderCell>
                            </ClaimsTableRow>
                        </ClaimsTableHeader>
                        <ClaimsTableBody>
                            <ClaimsTableRow v-for="(claim, index) in filteredCrawledClaims2" :key="index"
                                :class="{ 'blurred': isTestUser }">
                                <ClaimsTableCell>
                                    <StatusBadge :class="getProcessStatusClass(claim.claim_status || claim.처리상태)">
                                        {{ claim.claim_status || claim.처리상태 || '-' }}
                                    </StatusBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.branch_name || claim.처리지사 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatClaimDate(claim.claim_date || claim.접수일자) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.patient_number || claim.수진자주민번호 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <PatientName>{{ claim.patient_name || claim.수진자명 || '-' }}</PatientName>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <BenefitTypeBadge :class="getBenefitTypeClass(claim.benefit_type || claim.요양비종류)">
                                        {{ claim.benefit_type || claim.요양비종류 || '-' }}
                                    </BenefitTypeBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateRange>
                                        <DateItem>
                                            <DateLabel>시작일</DateLabel>
                                            <DateValue>{{ formatClaimDate(claim.start_date || claim.위임동의시작일) }}
                                            </DateValue>
                                        </DateItem>
                                        <DateItem>
                                            <DateLabel>종료일</DateLabel>
                                            <DateValue>{{ formatClaimDate(claim.end_date || claim.위임동의종료일) }}
                                            </DateValue>
                                        </DateItem>
                                    </DateRange>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.reject_reason || claim.보류반려사유 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <FinancialInfo>
                                        <FinancialItem>
                                            <FinancialLabel>금융기관:</FinancialLabel>
                                            <FinancialValue>{{ claim.financial_institution || claim.금융기관 || '-' }}
                                            </FinancialValue>
                                        </FinancialItem>
                                        <FinancialItem>
                                            <FinancialLabel>계좌번호:</FinancialLabel>
                                            <FinancialValue>{{ claim.account_number || claim.계좌번호 || '-' }}
                                            </FinancialValue>
                                        </FinancialItem>
                                        <FinancialItem>
                                            <FinancialLabel>예금주명:</FinancialLabel>
                                            <FinancialValue>{{ claim.account_holder || claim.예금주성명 || '-' }}
                                            </FinancialValue>
                                        </FinancialItem>
                                    </FinancialInfo>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.phone || claim.연락처 || '-' }}</ClaimsTableCell>
                            </ClaimsTableRow>
                        </ClaimsTableBody>
                    </ClaimsTable>

                    <!-- * 청구내역 테이블 -->
                    <ClaimsTable
                        v-else-if="currentClaimTab !== 'claim-list' && currentClaimTab !== 'assistance' && filteredCrawledClaims.length > 0"
                        class="oxygen-table">
                        <ClaimsTableHeader>
                            <ClaimsTableRow>
                                <ClaimsTableHeaderCell>지급상태</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래일시</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래기록사항</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>처리상태</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>접수일자</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>수진자명</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>사용기간</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>지급금액</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>지급취소/반송사유</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>최종지급일</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>사업자명</ClaimsTableHeaderCell>
                            </ClaimsTableRow>
                        </ClaimsTableHeader>
                        <ClaimsTableBody>
                            <ClaimsTableRow v-for="(claim, index) in filteredCrawledClaims" :key="index"
                                :class="{ 'blurred': isTestUser }">
                                <ClaimsTableCell>
                                    <StatusBadge :class="getPaymentStatusClass(getPaymentStatus(claim))">
                                        {{ getPaymentStatus(claim) }}
                                    </StatusBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ getPaymentTransactionDate(claim) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span style="font-size: 13px; color: #1f2937;">
                                        {{ getPaymentTransactionRemark(claim) }}
                                    </span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <StatusBadge :class="getProcessStatusClass(claim.claim_status || claim.처리상태)">
                                        {{ claim.claim_status || claim.처리상태 || '-' }}
                                    </StatusBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatClaimDate(claim.claim_date || claim.접수입력일자) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <PatientInfo2>
                                        <PatientName>{{ claim.patient_name || claim.수진자명 || '-' }}</PatientName>
                                        <PatientNumber>{{ claim.patient_number || claim.수진자주민번호 || '-' }}
                                        </PatientNumber>
                                    </PatientInfo2>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateRange>
                                        <DateItem>
                                            <DateLabel>사용시작일</DateLabel>
                                            <DateValue>{{ formatClaimDate(claim.start_date || claim.사용시작일) }}
                                            </DateValue>
                                        </DateItem>
                                        <DateItem>
                                            <DateLabel>사용종료일</DateLabel>
                                            <DateValue>{{ formatClaimDate(claim.end_date || claim.사용종료일) }}</DateValue>
                                        </DateItem>
                                    </DateRange>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <AmountDisplay>{{ formatClaimAmount(claim.amount || claim.지급금액) }}</AmountDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.reject_reason || claim.지급취소반송사유 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatClaimDate(claim.payment_date || claim.최종지급일) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <BusinessInfo>
                                        <BusinessName>{{ claim.business_name || claim.사업자명 || '-' }}</BusinessName>
                                        <BusinessNumber>{{ claim.business_number || claim.사업자등록번호 || '-' }}
                                        </BusinessNumber>
                                    </BusinessInfo>
                                </ClaimsTableCell>
                            </ClaimsTableRow>
                        </ClaimsTableBody>
                    </ClaimsTable>

                    <!-- * 보조기기 청구내역 테이블 -->
                    <ClaimsTable v-else-if="currentClaimTab === 'assistance' && filteredCrawledClaims3.length > 0">
                        <ClaimsTableHeader>
                            <ClaimsTableRow>
                                <ClaimsTableHeaderCell>처리상태</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>접수일자</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>보조기기품목</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>수급자</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>주민등록번호</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>지급금액</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>취소/반송사유</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>지급일자</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>판매업체명</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>관할지사</ClaimsTableHeaderCell>
                            </ClaimsTableRow>
                        </ClaimsTableHeader>
                        <ClaimsTableBody>
                            <ClaimsTableRow v-for="(claim, index) in filteredCrawledClaims3" :key="index"
                                :class="{ 'blurred': isTestUser }">
                                <ClaimsTableCell>
                                    <StatusBadge :class="getProcessStatusClass(claim.claim_status || claim.처리상태)">
                                        {{ claim.claim_status || claim.처리상태 || '-' }}
                                    </StatusBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatClaimDate(claim.claim_date || claim.접수일자) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <BenefitTypeBadge :class="getBenefitTypeClass(claim.benefit_type || claim.보조기기품목)">
                                        {{ claim.benefit_type || claim.보조기기품목 || '-' }}
                                    </BenefitTypeBadge>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <PatientName>{{ claim.patient_name || claim.급여를받을사람 || '-' }}</PatientName>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.patient_number || claim.주민등록번호 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <AmountDisplay>{{ formatClaimAmount(claim.amount || claim.지급금액) }}</AmountDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.reject_reason || claim.취소반송사유 || '-' }}</ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatClaimDate(claim.payment_date || claim.지급일자) }}</DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <BusinessInfo>
                                        <BusinessNumber>{{ claim.business_number || claim.사업자등록번호 || '-' }}
                                        </BusinessNumber>
                                        <BusinessName>{{ claim.business_name || claim.판매업체명 || '-' }}</BusinessName>
                                    </BusinessInfo>
                                </ClaimsTableCell>
                                <ClaimsTableCell>{{ claim.branch_area || claim.관할지사 || '-' }}</ClaimsTableCell>
                            </ClaimsTableRow>
                        </ClaimsTableBody>
                    </ClaimsTable>

                    <!-- * 크롤링된 데이터가 없을 때 -->
                    <EmptyState
                        v-else-if="crawledClaimData.length === 0 && crawledClaimData2.length === 0 && crawledClaimData3.length === 0">
                        <EmptyIcon>🗂️</EmptyIcon>
                        <EmptyTitle>청구 내역이 없습니다</EmptyTitle>
                        <EmptyDescription>청구내역 조회 버튼을 클릭하여 공단에서 데이터를 가져오세요</EmptyDescription>
                    </EmptyState>
                </ClaimsCard>

                <!-- <ClaimsCard>
                    <ClaimsHeader>
                        <ClaimsTitle>
                            입출금내역
                            <span v-if="bankTransactionFilter !== 'all'">
                                ({{ filteredBankTransactions.length }}건 / 전체 {{ bankTransactions.length }}건)
                            </span>
                            <span v-else>
                                ({{ bankTransactions.length }}건)
                            </span>
                            <ClaimListButton @click="loadBankTransactions">
                                <History :size="20" :style="{ paddingTop: '3px' }" />
                                입출금내역 조회
                            </ClaimListButton>
                        </ClaimsTitle>
                        <ClaimsActions>
                            <FilterSelect v-model="bankTransactionFilter" @change="handleBankTransactionFilterChange">
                                <option value="all">전체</option>
                                <option value="deposit">입금</option>
                                <option value="withdrawal">출금</option>
                            </FilterSelect>

                            <FilterSelect v-model="quickDateSelect" @change="handleQuickDateSelect">
                                <option value="">직접 선택</option>
                                <option value="today">당일</option>
                                <option value="yesterday">전일</option>
                                <option value="3days">3일</option>
                                <option value="1week">1주</option>
                                <option value="15days">15일</option>
                                <option value="1month">1개월</option>
                                <option value="3months">3개월</option>
                                <option value="6months">6개월</option>
                            </FilterSelect>

                            <DateSelectionGroup>
                                <DateInput type="date" v-model="bankTransactionDateRange.startDate"
                                    :max="bankTransactionDateRange.endDate || today"
                                    @change="handleBankTransactionStartDateChange" />
                                <DateSeparator>~</DateSeparator>
                                <DateInput type="date" v-model="bankTransactionDateRange.endDate"
                                    :min="bankTransactionDateRange.startDate" :max="today"
                                    @change="handleBankTransactionEndDateChange" />
                            </DateSelectionGroup>
                        </ClaimsActions>
                    </ClaimsHeader>

                    <ClaimsTable v-if="filteredBankTransactions && filteredBankTransactions.length > 0">
                        <ClaimsTableHeader>
                            <ClaimsTableRow>
                                <ClaimsTableHeaderCell>순번</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래일시</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>출금금액</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>입금금액</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>잔액</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래내용</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래기록사항</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래번호</ClaimsTableHeaderCell>
                                <ClaimsTableHeaderCell>거래메모</ClaimsTableHeaderCell>
                            </ClaimsTableRow>
                        </ClaimsTableHeader>
                        <ClaimsTableBody>
                            <ClaimsTableRow v-for="(transaction, index) in filteredBankTransactions" :key="index"
                                :class="{ 'blurred': isTestUser }">
                                <ClaimsTableCell>
                                    <span style="font-weight: 600; color: #64748b;">{{ index + 1 }}</span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <DateDisplay>{{ formatTransactionDateTime(transaction.transactionDate) }}
                                    </DateDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span v-if="transaction.withdrawalAmount > 0"
                                        style="color: #dc2626; font-weight: 600;">
                                        {{ formatPrice(transaction.withdrawalAmount) }}원
                                    </span>
                                    <span v-else>-</span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span v-if="transaction.depositAmount > 0"
                                        style="color: #2563eb; font-weight: 600;">
                                        {{ formatPrice(transaction.depositAmount) }}원
                                    </span>
                                    <span v-else>-</span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <AmountDisplay>{{ formatPrice(transaction.balance) }}원</AmountDisplay>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span style="font-weight: 500; color: #1f2937;">{{ transaction.transType ||
                                        '-' }}</span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span style="font-size: 13px; color: #1f2937;">
                                        {{ transaction.transRemark1 || '-' }}
                                    </span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span style="font-size: 13px; color: #6b7280;">{{ transaction.transOffice ||
                                        '-' }}</span>
                                </ClaimsTableCell>
                                <ClaimsTableCell>
                                    <span
                                        style="font-size: 13px; color: #6b7280; cursor: pointer; text-decoration: underline;"
                                        @click="editTransactionMemo(transaction)"
                                        :title="transaction.transactionMemo ? '클릭하여 메모 수정' : '클릭하여 메모 작성'">
                                        {{ transaction.transactionMemo || '메모 작성' }}
                                    </span>
                                </ClaimsTableCell>
                            </ClaimsTableRow>
                        </ClaimsTableBody>
                    </ClaimsTable>

                    <EmptyState v-else-if="filteredBankTransactions.length === 0 && bankTransactions.length > 0">
                        <EmptyIcon>🔍</EmptyIcon>
                        <EmptyTitle>필터링된 결과가 없습니다</EmptyTitle>
                        <EmptyDescription>선택한 필터 조건에 맞는 입출금내역이 없습니다. 다른 조건을 선택해보세요.</EmptyDescription>
                    </EmptyState>

                    <EmptyState v-else-if="bankTransactions.length === 0">
                        <EmptyIcon>💰</EmptyIcon>
                        <EmptyTitle>입출금내역이 없습니다</EmptyTitle>
                        <EmptyDescription>입출금내역 조회 버튼을 클릭하여 은행에서 데이터를 가져오세요</EmptyDescription>
                    </EmptyState>
                </ClaimsCard> -->
            </RightSection>
        </MainContent>

        <!-- * 청구자 등록 모달 -->
        <RecipientRegistrationModal v-if="showRegistrationModal" @close="showRegistrationModal = false"
            @submit="handleRegistrationSuccess" />
        <!-- * 파일 상세 정보 모달 -->
        <FileDetailModal v-if="showFileDetailModal" :file="selectedFile" @close="showFileDetailModal = false" />
        <!-- * 청구 서류 안내 모달 -->
        <RequiredDocumentsModal v-if="showRequiredDocumentsModal" :claimType="currentFileTab"
            @close="showRequiredDocumentsModal = false" />
        <!-- * 청구 자동화 옵션 모달 -->
        <!-- <ModalPage v-if="showSettingsModal" :show="showSettingsModal" width="30%" height="45%"
            @update:show="showSettingsModal = $event">
            <ClaimOption :show="showSettingsModal" :settings="automationSettings" @close="showSettingsModal = false"
                @settings-changed="handleSettingsChanged" @settings-deleted="handleSettingsDeleted" />
        </ModalPage> -->
        <!-- * 주문서 수정 모달 -->
        <ModalPage v-if="showOrderModifyModal" :show="showOrderModifyModal" width="95%" height="80%"
            @update:show="closeOrderModifyModal">
            <OrderModifyPage :orderId="selectedPatientForOrder?.id || ''" :orderData="selectedPatientForOrder || {}"
                @close-modal="closeOrderModifyModal" @order-updated="handleOrderModifyComplete" />
        </ModalPage>
    </PageContainer>
</template>

<script>
import * as ClaimPageCss from '@/assets/styles/claim/ClaimPageCss.js';
import ModalPage from '@/components/layout/ModalPage.vue';
import FileDetailModal from '@/components/claim/modal/FileDetailModal.vue';
import RequiredDocumentsModal from '@/components/claim/modal/RequiredDocumentsModal.vue';
import RecipientRegistrationModal from '@/components/claim/modal/RecipientRegistrationModal.vue';
// import ClaimOption from '@/components/claim/modal/ClaimOption.vue';
import OrderModifyPage from '@/components/order/OrderModifyPage.vue';

import Magnify from 'vue-material-design-icons/Magnify.vue';
import FileDocumentPlusOutline from 'vue-material-design-icons/FileDocumentPlusOutline.vue';
import FilePlus from 'vue-material-design-icons/FilePlus.vue';
import AccountPlus from 'vue-material-design-icons/AccountPlus.vue';
import NoteAlert from 'vue-material-design-icons/NoteAlert.vue';
import TextSearch from 'vue-material-design-icons/TextSearch.vue';
import History from 'vue-material-design-icons/History.vue';
import ListBoxOutline from 'vue-material-design-icons/ListBoxOutline.vue';
import Monitor from 'vue-material-design-icons/Monitor.vue';
import Cog from 'vue-material-design-icons/Cog.vue';

export default {
    name: 'ClaimPage',

    inject: ['globalMessageSystem'],

    components: {
        ...ClaimPageCss,
        OrderModifyPage,
        ModalPage,
        Magnify,
        FileDocumentPlusOutline,
        FilePlus,
        AccountPlus,
        NoteAlert,
        TextSearch,
        History,
        ListBoxOutline,
        FileDetailModal,
        RequiredDocumentsModal,
        RecipientRegistrationModal,
        Monitor,
        Cog
    },

    data() {
        return {
            showAddClaimModal: false,
            showFileDetailModal: false,
            showRequiredDocumentsModal: false,
            showRegistrationModal: false,
            // showSettingsModal: false,
            selectedFile: null,

            // 주문서 수정 모달 관련
            showOrderModifyModal: false,
            selectedPatientForOrder: null,

            // 청구 일정 관련 데이터
            scheduleFilter: 'all',
            currentMonth: new Date().getMonth(),
            currentYear: new Date().getFullYear(),
            selectedDay: null,
            upcomingClaims: [],

            // 달력 데이터
            calendarDays: [],

            currentProcessingPatient: null, // 현재 처리 중인 환자
            failedPatients: [], // 실패한 환자 목록

            // 청구 내역
            claims: [],
            claimsFilter: 'all',

            // 현재 청구 내역 탭
            currentClaimTab: 'claim-list',

            // 청구 내역 탭 정의 (당뇨소모성재료 제거)
            claimTabs: [
                { value: 'claim-list', label: '청구위임내역' },
                { value: 'oxygen', label: '산소발생기 청구내역' },
                { value: 'assistance', label: '보조기기 청구내역' }
            ],

            // 크롤링 관련 상태
            isCrawling: false,
            progressListener: null,

            // 청구내역 관련
            crawledClaimData: [],
            crawledClaimData2: [],
            crawledClaimData3: [],
            totalCount: 0,
            claimDateRange: {
                startDate: '',
                endDate: ''
            },

            // 산소발생기 필터 상태
            oxygenFilter: 'all',

            // 입출금내역 관련
            bankTransactions: [],
            bankTransactionFilter: 'all',
            bankTransactionDateRange: {
                startDate: '',
                endDate: ''
            },
            quickDateSelect: '',

            // 청구 자동화 관련 데이터
            isAutomationActive: false,
            automationSettings: null,
            intervalId: null,
            timeoutId: null,

            // 산소발생기 자동화 관련
            oxygenPatients: [],
            selectedOxygenPatient: null,
            isLoadingOxygenPatients: false,
            oxygenStats: {
                totalPatients: 0,
                processedToday: 0,
                pending: 0,
                error: 0
            },
            oxygenProcessingList: [],

            // 실패 로그 관련
            automationFailures: [],
            isLoadingFailures: false,

            // 자동화 처리 관련 (추가)
            processingQueue: [], // 처리 대기열
            processedPatients: new Set(), // 처리된 환자 ID Set
            processingLock: false, // 처리 중 락

            // 툴팁 관련 추가
            tooltipDay: null,
            tooltipPosition: { x: 0, y: 0 },
            isTooltipPinned: false, // 클릭으로 고정되었는지 여부

            // 환자명 검색 관련
            searchQuery: '', // 환자명 검색어
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },

        currentMonthYear() {
            const monthNames = [
                '1월', '2월', '3월', '4월', '5월', '6월',
                '7월', '8월', '9월', '10월', '11월', '12월'
            ];
            return `${this.currentYear}년 ${monthNames[this.currentMonth]}`;
        },

        filteredUpcomingClaims() {
            if (this.scheduleFilter === 'all') {
                return this.upcomingClaims;
            }
            return this.upcomingClaims.filter(claim => claim.type === this.scheduleFilter);
        },

        // 산소발생기 필터링된 환자 목록
        filteredOxygenPatients() {
            return this.oxygenPatients.filter(patient => {
                const status = patient.status || patient.claimStatus;

                // 검색어로 필터링
                if (this.searchQuery && this.searchQuery.trim() !== '') {
                    const searchTerm = this.searchQuery.trim().toLowerCase();
                    const patientName = (patient.name || '').toLowerCase();

                    if (!patientName.includes(searchTerm)) {
                        return false;
                    }
                }

                switch (this.oxygenFilter) {
                    case 'pending':
                        if (status === '환자등록' || status === '위임내역등록' || status === '청구등록') {
                            return true;
                        }
                        return false;
                    case 'completed':
                        return status === '청구완료';
                    case 'error':
                        return status === 'error' || status === 'failed';
                    case 'failed':
                        // 서버 실패 로그만 확인
                        return this.automationFailures.some(failure => failure.patient_id === patient.id);
                    default:
                        return true;
                }
            });
        },

        // 크롤링된 청구 내역 필터링 (산소발생기, 보조기기용)
        filteredCrawledClaims() {
            if (this.currentClaimTab === 'claim-list') {
                return [];
            }

            if (this.crawledClaimData && Array.isArray(this.crawledClaimData)) {
                let filteredData = this.crawledClaimData.filter(claim => {
                    const benefitType = claim.benefit_type || claim.요양비종류 || '';

                    if (!benefitType) return false;

                    switch (this.currentClaimTab) {
                        case 'oxygen':
                            return benefitType.includes('산소') || benefitType.includes('산소치료') || benefitType.includes('O2');
                        default:
                            return true;
                    }
                });

                if (this.claimsFilter !== 'all') {
                    filteredData = filteredData.filter(claim => {
                        const status = claim.claim_status || claim.처리상태 || '';
                        return this.matchesStatusFilter(status, this.claimsFilter);
                    });
                }

                return filteredData;
            }

            return [];
        },

        // 청구위임내역 필터링 (claimData2)
        filteredCrawledClaims2() {
            if (this.currentClaimTab !== 'claim-list') {
                return [];
            }

            if (this.crawledClaimData2 && Array.isArray(this.crawledClaimData2)) {
                let filteredData = this.crawledClaimData2;

                // 처리상태 필터 적용
                if (this.claimsFilter !== 'all') {
                    filteredData = filteredData.filter(claim => {
                        const status = claim.claim_status || claim.처리상태 || '';
                        return this.matchesStatusFilter(status, this.claimsFilter);
                    });
                }

                return filteredData;
            }

            return [];
        },

        // 보조기기 청구내역 필터링 (claimData3)
        filteredCrawledClaims3() {
            if (this.currentClaimTab !== 'assistance') {
                return [];
            }

            if (this.crawledClaimData3 && Array.isArray(this.crawledClaimData3)) {
                let filteredData = this.crawledClaimData3;

                // 처리상태 필터 적용
                if (this.claimsFilter !== 'all') {
                    filteredData = filteredData.filter(claim => {
                        const status = claim.claim_status || claim.처리상태 || '';
                        return this.matchesStatusFilter(status, this.claimsFilter);
                    });
                }

                console.log('보조기기 탭 필터링 결과:', filteredData.length);
                return filteredData;
            }

            return [];
        },

        today() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },

        isDateRangeValid() {
            if (!this.claimDateRange.startDate || !this.claimDateRange.endDate) {
                return false;
            }

            const startDate = new Date(this.claimDateRange.startDate);
            const endDate = new Date(this.claimDateRange.endDate);

            // 시작일이 종료일보다 늦으면 안됨
            if (startDate > endDate) {
                return false;
            }

            // 최대 1달(31일) 간격 제한
            const maxDays = 31;
            const diffTime = endDate.getTime() - startDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            return diffDays <= maxDays;
        },

        filteredClaims() {
            let filtered = this.claims;

            // 상태 필터 적용
            if (this.claimsFilter !== 'all') {
                filtered = filtered.filter(claim => claim.status === this.claimsFilter);
            }

            // 탭 필터 적용
            if (this.currentClaimTab !== 'all') {
                filtered = filtered.filter(claim => claim.type === this.currentClaimTab);
            }

            return filtered;
        },

        claimStats() {
            const total = this.claims.length;
            const totalAmount = this.claims.reduce((sum, claim) => sum + claim.amount, 0);
            const approved = this.claims.filter(claim =>
                claim.status === 'approved' || claim.status === 'paid'
            ).length;
            const approvedAmount = this.claims
                .filter(claim => claim.status === 'approved' || claim.status === 'paid')
                .reduce((sum, claim) => sum + claim.amount, 0);

            return {
                total,
                totalAmount,
                approved,
                approvedAmount
            };
        },

        // 입출금내역 필터링
        filteredBankTransactions() {
            let filtered = this.bankTransactions;

            // 거래 유형 필터 적용
            if (this.bankTransactionFilter !== 'all') {
                filtered = filtered.filter(transaction => {
                    if (this.bankTransactionFilter === 'deposit') {
                        // 입금만: depositAmount > 0
                        return parseFloat(transaction.depositAmount || 0) > 0;
                    } else if (this.bankTransactionFilter === 'withdrawal') {
                        // 출금만: withdrawalAmount > 0
                        return parseFloat(transaction.withdrawalAmount || 0) > 0;
                    }
                    return true;
                });
            }

            // 날짜 범위 필터 적용
            if (this.bankTransactionDateRange.startDate && this.bankTransactionDateRange.endDate) {
                const startDate = new Date(this.bankTransactionDateRange.startDate);
                const endDate = new Date(this.bankTransactionDateRange.endDate);

                filtered = filtered.filter(transaction => {
                    // YYYYMMDDHHMMSS 형식을 Date 객체로 변환
                    const transDate = this.parseTransactionDate(transaction.transactionDate);
                    if (!transDate) return false;

                    return transDate >= startDate && transDate <= endDate;
                });
            }

            return filtered;
        },

        // 자동화 버튼 텍스트 결정
        getAutomationButtonText() {
            if (this.isAutomationActive) {
                // if (this.automationSettings) {
                //     if (this.automationSettings.settingType === 'interval') {
                //         return `자동화 중지 (${this.automationSettings.checkInterval}분 간격)`;
                //     } else if (this.automationSettings.settingType === 'time') {
                //         return `자동화 중지 (${this.automationSettings.startTime} 실행)`;
                //     }
                // }
                return '자동화 중지';
            } else {
                // if (this.automationSettings) {
                //     if (this.automationSettings.settingType === 'interval') {
                //         return `자동화 시작 (${this.automationSettings.checkInterval}분 간격)`;
                //     } else if (this.automationSettings.settingType === 'time') {
                //         return `자동화 시작 (${this.automationSettings.startTime} 실행)`;
                //     }
                // }
                return '자동화 시작';
            }
        }
    },

    methods: {
        // 산소발생기 환자 목록 새로고침 핸들러
        handleRefreshOxygenPatients() {
            this.loadOxygenPatients();
        },

        // ================================================================
        // 검색 관련
        // ================================================================
        // 환자명 검색 입력 핸들러
        handleSearchInput() {
            // 검색어 변경 시 필터링
        },

        // 검색어 지우기
        clearSearch() {
            this.searchQuery = '';
        },

        // ================================================================
        // 자동화 설정 관련
        // ================================================================
        // 자동화 설정 로드 (로컬 스토리지에서)
        // async loadAutomationSettings() {
        //     try {
        //         // ✅ localStorage에서 직접 로드
        //         const savedSettings = localStorage.getItem('automationSettings');

        //         if (savedSettings) {
        //             this.automationSettings = JSON.parse(savedSettings);
        //         } else {
        //             // 설정이 없으면 기본값 설정
        //             this.automationSettings = {
        //                 settingType: 'interval',
        //                 checkInterval: 60,
        //                 startTime: '09:00',
        //                 isActive: false
        //             };
        //         }
        //     } catch (error) {
        //         console.error('[ClaimPage] 자동화 설정 로드 실패:', error);

        //         // 에러 발생 시에도 기본값 설정
        //         this.automationSettings = {
        //             settingType: 'interval',
        //             checkInterval: 60,
        //             startTime: '09:00',
        //             isActive: false
        //         };
        //     }
        // },

        // 자동화 설정 저장 (로컬 스토리지에)
        // saveAutomationSettings(settings) {
        //     try {
        //         this.automationSettings = settings;
        //         localStorage.setItem('automationSettings', JSON.stringify(settings));

        //         this.$swal.fire({
        //             icon: 'success',
        //             title: '설정 저장 완료',
        //             text: '자동화 설정이 저장되었습니다.',
        //             timer: 2000,
        //             showConfirmButton: false
        //         });
        //         return true;
        //     } catch (error) {
        //         console.error('자동화 설정 저장 실패:', error);
        //         this.$swal.fire({
        //             icon: 'error',
        //             title: '설정 저장 실패',
        //             text: '설정 저장 중 오류가 발생했습니다.',
        //             confirmButtonText: '확인'
        //         });
        //         return false;
        //     }
        // },

        // 설정 변경 처리
        // async handleSettingsChanged(newSettings) {
        //     const success = this.saveAutomationSettings(newSettings);

        //     if (success) {
        //         // 설정이 활성화되어 있고 자동화가 실행 중이면 재시작
        //         if (newSettings.isActive && this.isAutomationActive) {
        //             await this.stopAutomation();
        //         }
        //     }
        // },

        // 설정 삭제 처리
        // handleSettingsDeleted() {
        //     // 자동화가 실행 중이면 중지
        //     if (this.isAutomationActive) {
        //         this.stopAutomation();
        //     }

        //     // 설정 초기화
        //     this.automationSettings = {
        //         settingType: 'interval',
        //         checkInterval: 60,
        //         startTime: '09:00',
        //         isActive: false
        //     };

        //     this.$swal.fire({
        //         icon: 'success',
        //         title: '설정 삭제 완료',
        //         text: '자동화 설정이 삭제되었습니다.',
        //         timer: 2000,
        //         showConfirmButton: false
        //     });
        // },

        // ================================================================
        // 자동화 실행 관련
        // ================================================================
        // 자동화 토글
        async toggleOverallAutomation() {
            if (this.isAutomationActive) {
                await this.stopAutomation();
            } else {
                await this.startAutomation();
            }
        },

        // 자동화 시작
        async startAutomation() {
            try {
                if (this.isAutomationActive || this.processingLock) {
                    this.$swal.fire({
                        icon: 'warning',
                        title: '자동화 진행 중',
                        text: '이미 청구 자동화가 진행 중입니다.',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                // 상태 초기화
                this.isAutomationActive = true;
                this.processingLock = true;
                this.currentProcessingPatient = null;
                this.processedPatients.clear();

                // 청구 시작 알림
                this.showAutomationStartNotification();

                // 환자 데이터 구성
                await this.initializeProcessingQueue();
                const processedPatients = await this.buildProcessedPatients();

                if (processedPatients.length === 0) {
                    this.$swal.fire({
                        icon: 'info',
                        title: '처리할 환자 없음',
                        text: '현재 처리할 환자가 없습니다.',
                        confirmButtonText: '확인'
                    });
                    // 상태 초기화
                    this.isAutomationActive = false;
                    this.processingLock = false;
                    this.currentProcessingPatient = null;
                    return;
                }

                // 환자 순차 처리
                await this.processPatientQueue(processedPatients);

            } catch (error) {
                console.error('[ClaimPage] 자동화 시작 실패:', error);
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'startAutomation'
                });
                this.$swal.fire({
                    icon: 'error',
                    title: '자동화 시작 실패',
                    text: error.message,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isAutomationActive = false;
                this.processingLock = false;
                this.currentProcessingPatient = null;
            }
        },

        // 자동화 중지 함수 추가
        async stopAutomation() {
            try {
                // 자동화 상태 중지
                this.isAutomationActive = false;
                this.processingLock = false;

                // 현재 처리 중인 환자 초기화
                this.currentProcessingPatient = null;

                // 처리 대기열 초기화
                this.processingQueue = [];
                this.processedPatients.clear();

                // 타이머 정리
                if (this.intervalId) {
                    clearInterval(this.intervalId);
                    this.intervalId = null;
                }

                if (this.timeoutId) {
                    clearTimeout(this.timeoutId);
                    this.timeoutId = null;
                }

                // 중지 알림 표시
                this.showAutomationStopNotification();

                this.$sentry.logWithComponent('ClaimPage', 'info', '자동화 중지', {
                    timestamp: new Date().toISOString()
                });

            } catch (error) {
                console.error('[ClaimPage] 자동화 중지 실패:', error);
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'stopAutomation'
                });
            }
        },

        // ================================================================
        // 환자 처리 관련
        // ================================================================
        // 환자 순차 처리
        async processPatientQueue(processedPatients = null) {
            let successCount = 0;
            let failureCount = 0;

            // 매개변수로 받은 환자 목록이 있으면 사용, 없으면 기존 대기열 사용
            const patientsToProcess = processedPatients || this.processingQueue;

            for (let i = 0; i < patientsToProcess.length; i++) {
                // 중지 확인
                if (!this.isAutomationActive) {
                    break;
                }

                const patient = patientsToProcess[i];

                // 이미 처리된 환자는 건너뛰기
                if (this.processedPatients.has(patient.id)) {
                    continue;
                }

                console.log(`\n[Queue] [${i + 1}/${patientsToProcess.length}] ${patient.name} 처리 시작`);

                // 환자 처리
                const result = await this.processSinglePatient(patient);

                if (result.success) {
                    successCount++;
                    this.processedPatients.add(patient.id);
                } else {
                    failureCount++;
                }

                // 다음 환자 처리 전 대기
                if (i < patientsToProcess.length - 1) {
                    await this.sleep(2000);
                }
            }

            // 완료 알림
            this.showAutomationCompleteNotification(successCount, failureCount);
        },

        // 단일 환자 처리
        async processSinglePatient(patient) {
            const startTime = Date.now();

            try {
                const status = patient.status || patient.claimStatus;

                let nextStep, taskType;

                // buildProcessedPatients에서 설정한 nextStep과 taskType이 있으면 우선 사용
                if (patient.nextStep && patient.taskType) {
                    nextStep = patient.nextStep;
                    taskType = patient.taskType;
                    console.log(`[ClaimPage] ${patient.name} - buildProcessedPatients에서 설정된 값 사용: nextStep=${nextStep}, taskType=${taskType}, status=${status}`);
                } else {
                    // buildProcessedPatients를 거치지 않은 경우에만 getNextStep 사용
                    nextStep = this.getNextStep(status);
                    taskType = this.mapStepToTaskType(nextStep);
                    console.log(`[ClaimPage] ${patient.name} - getNextStep으로 계산: nextStep=${nextStep}, taskType=${taskType}, status=${status}`);
                }

                // 디버깅: nextStep 확인
                if (status === '환자등록' && nextStep !== '위임내역등록') {
                    console.error(`[ClaimPage] ${patient.name} - 환자등록 상태인데 nextStep이 '위임내역등록'이 아님: nextStep=${nextStep}, status=${status}`);
                    // 강제로 위임내역등록으로 설정
                    nextStep = '위임내역등록';
                    taskType = this.mapStepToTaskType(nextStep);
                    console.log(`[ClaimPage] ${patient.name} - nextStep을 '위임내역등록'으로 강제 수정`);
                }

                if (nextStep === '청구완료' && status !== '청구등록') {
                    return { success: true, skipped: true };
                }

                this.currentProcessingPatient = {
                    id: patient.id,
                    name: patient.name,
                    step: nextStep
                };

                // 청구 시작 알림
                this.showPatientClaimStartNotification(patient.name, nextStep);

                const stepResult = await this.executePatientStep(patient, nextStep);

                if (stepResult.success) {
                    // 청구등록 성공 시 상태를 "청구완료"로 변경
                    const statusToUpdate = nextStep === '청구등록' ? '청구완료' : nextStep;

                    // 상태 업데이트
                    await this.updatePatientStatus(patient.id, statusToUpdate);

                    // 청구 완료 알림
                    this.showPatientClaimCompleteNotification(patient.name, nextStep);

                    return { success: true };
                } else {
                    throw new Error(stepResult.error || '실행 실패');
                }

            } catch (error) {
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);

                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'processSinglePatient',
                    patientId: patient.id,
                    patientName: patient.name,
                    step: patient.nextStep || this.getNextStep(patient.status || patient.claimStatus),
                    duration: duration
                });

                // 실패 알림
                this.showPatientClaimFailureNotification(
                    patient.name,
                    patient.nextStep || this.getNextStep(patient.status || patient.claimStatus),
                    error.message
                );

                // 실패 로그 저장
                await this.saveFailureLog(
                    patient,
                    patient.nextStep || this.getNextStep(patient.status || patient.claimStatus),
                    error.message,
                    {
                        type: 'crawling_error',
                        message: error.message,
                        statusCode: error.response?.status,
                        claimId: patient.requestData?.claimId,
                        stack: error.stack,
                        duration: duration
                    }
                );

                return {
                    success: false,
                    error: error.message
                };
            }
        },

        // 환자 단계 실행 
        async executePatientStep(patient, step) {
            try {
                // 단계를 taskType으로 변환
                const taskType = this.mapStepToTaskType(step);

                if (!taskType) {
                    throw new Error(`지원하지 않는 단계: ${step}`);
                }

                // 요청 데이터 구성 - patient.requestData가 있으면 사용, 없으면 새로 생성
                const requestData = patient.requestData || this.buildRequestData(patient, taskType);

                // 크롤링 실행
                const result = await window.electronAPI.startCrawling(requestData);

                if (result.success) {
                    return { success: true };
                } else {
                    // 백엔드에서 받은 구체적인 에러 메시지 전달
                    const errorMessage = result.error || result.message || '크롤링 실패';
                    console.error(`[ClaimPage] 크롤링 실패: ${patient.name} - ${step}:`, {
                        error: errorMessage,
                        result: result
                    });
                    throw new Error(errorMessage);
                }

            } catch (error) {
                console.error(`[ClaimPage] 환자 단계 실행 실패: ${patient.name} - ${step}:`, error);
                return {
                    success: false,
                    error: error.message
                };
            }
        },

        // 처리 대기열 생성
        async initializeProcessingQueue() {
            this.processingQueue = [];
            this.processedPatients.clear();

            // 중복 제거용 Map
            const patientMap = new Map();

            // 청구 대기 상태의 환자만 필터링 (위임내역등록, 청구등록만)
            const pendingStatuses = ['환자등록', '위임내역등록', '청구등록'];

            for (const patient of this.oxygenPatients) {
                // 상태 값 정규화 (공백 제거, trim)
                let status = (patient.status || patient.claimStatus || '').toString().trim();

                // 빈 상태는 건너뛰기
                if (!status) {
                    console.warn(`[ClaimPage] 환자 ${patient.name} (ID: ${patient.id})의 상태가 비어있습니다.`);
                    continue;
                }

                // 청구 대기 상태인 환자만 처리 (위임내역등록, 청구등록)
                if (pendingStatuses.includes(status)) {
                    // 중복 체크 (ID로만 체크)
                    if (patientMap.has(patient.id)) {
                        continue;
                    }

                    patientMap.set(patient.id, true);
                    this.processingQueue.push(patient);
                }
            }
        },

        // 단계별 파일 필터링 함수 수정
        filterFilesByStep(files, step, patient = null) {
            if (!files || files.length === 0) {
                return [];
            }

            const fileName = (file) => {
                return (file.fileName || file.file_name || file.name || '').toLowerCase();
            };

            switch (step) {
                case '위임내역등록':
                    return files.filter(file => {
                        const name = fileName(file);
                        return name.includes('신분증') ||
                            name.includes('위임장');
                    });

                case '청구등록':
                    // taxInvoice와 cashReceipt 값 확인
                    const hasTaxInvoice = patient && (
                        (patient.tax_invoice && patient.tax_invoice.trim() !== '') ||
                        (patient.taxInvoice && patient.taxInvoice.trim() !== '')
                    );
                    const hasCashReceipt = patient && (
                        (patient.cash_receipt && patient.cash_receipt.trim() !== '') ||
                        (patient.cashReceipt && patient.cashReceipt.trim() !== '')
                    );

                    return files.filter(file => {
                        const name = fileName(file);

                        // 세금계산서 파일 제외 (taxInvoice 값이 있으면)
                        if (hasTaxInvoice && name.includes('세금계산서')) {
                            console.log(`[ClaimPage] 환자 ${patient.name} - 세금계산서 데이터가 있어 파일 제외: ${name}`);
                            return false;
                        }

                        // 현금영수증 파일 제외 (cashReceipt 값이 있으면)
                        if (hasCashReceipt && name.includes('현금영수증')) {
                            console.log(`[ClaimPage] 환자 ${patient.name} - 현금영수증 데이터가 있어 파일 제외: ${name}`);
                            return false;
                        }

                        // 나머지 파일은 포함
                        return name.includes('처방전') ||
                            name.includes('세금계산서') ||
                            name.includes('현금영수증') ||
                            name.includes('표준계약서');
                    });

                default:
                    return files;
            }
        },

        // 환자 데이터 구성 로직 분리
        async buildProcessedPatients() {
            const processedPatients = [];

            for (const patient of this.processingQueue) {
                const status = patient.status || patient.claimStatus;
                const nextStep = this.getNextStep(status);
                const taskType = this.mapStepToTaskType(nextStep);

                // '청구등록' 상태는 nextStep이 '완료'이지만 실제로는 '청구등록' 작업을 수행해야 함
                if (status === '청구등록') {
                    // '청구등록' 상태는 'oxygen-claim' 작업을 수행
                    const requestData = this.buildRequestData(patient, 'oxygen-claim');

                    // 파일 처리 (주문서 파일 가져오기)
                    let savedFiles = [];
                    const allFiles = await this.getSelectedFilesForOrder(patient.orderId);

                    // 단계별 파일 필터링
                    const filteredFiles = this.filterFilesByStep(allFiles, '청구등록', patient);

                    if (filteredFiles && filteredFiles.length > 0) {
                        const fileData = await Promise.all(
                            filteredFiles.map(async (doc) => {
                                try {
                                    const response = await fetch(doc.url || doc.file_path || doc.filePath);
                                    const arrayBuffer = await response.arrayBuffer();
                                    const uint8Array = new Uint8Array(arrayBuffer);

                                    return {
                                        name: doc.fileName || doc.file_name || 'unknown',
                                        data: Array.from(uint8Array),
                                        size: doc.size || 0,
                                        type: doc.type || doc.file_type || 'application/octet-stream',
                                        documentType: doc.documentType || doc.document_type || 'unknown'
                                    };
                                } catch (error) {
                                    console.error(`[ClaimPage] 환자 ${patient.name} - S3 파일 다운로드 실패: ${doc.fileName}`, error);
                                    return null;
                                }
                            })
                        );

                        const validFileData = fileData.filter(file => file !== null);

                        if (window.electronAPI && window.electronAPI.saveCrawlingFiles && validFileData.length > 0) {
                            const claimIdStr = String(patient.id || `claim_${Date.now()}`);
                            const saveResult = await window.electronAPI.saveCrawlingFiles({
                                files: validFileData,
                                claimId: claimIdStr
                            });

                            if (saveResult.success) {
                                savedFiles = saveResult.files;
                            } else {
                                console.error(`[ClaimPage] 환자 ${patient.name} - 일렉트론 파일 저장 실패:`, saveResult);
                            }
                        }
                    } else {
                        console.log(`[ClaimPage] 환자 ${patient.name} - 청구등록 단계에 해당하는 파일이 없음`);
                    }

                    requestData.files = savedFiles.map(file => file.path || file.name);

                    if (!requestData || !requestData.claimId) {
                        console.error(`[ClaimPage] 환자 ${patient.name}의 requestData 구성 실패:`, requestData);
                        continue;
                    }

                    processedPatients.push({
                        ...patient,
                        requestData: requestData,
                        nextStep: '청구등록',
                        taskType: 'oxygen-claim'
                    });
                } else if (nextStep !== '청구완료') {
                    // 기존 로직 (위임내역등록, 환자등록 등)
                    // 파일 처리 (주문서 파일 가져오기)
                    let savedFiles = [];

                    // 주문서 ID로 파일 정보 가져오기 (S3 URL 포함)
                    const allFiles = await this.getSelectedFilesForOrder(patient.orderId);

                    // 단계별 파일 필터링
                    const filteredFiles = this.filterFilesByStep(allFiles, nextStep, patient);

                    if (filteredFiles && filteredFiles.length > 0) {
                        const fileData = await Promise.all(
                            filteredFiles.map(async (doc) => {
                                try {
                                    // S3 URL에서 파일 다운로드
                                    const response = await fetch(doc.url || doc.file_path || doc.filePath);
                                    const arrayBuffer = await response.arrayBuffer();
                                    const uint8Array = new Uint8Array(arrayBuffer);

                                    return {
                                        name: doc.fileName || doc.file_name || 'unknown',
                                        data: Array.from(uint8Array),
                                        size: doc.size || 0,
                                        type: doc.type || doc.file_type || 'application/octet-stream',
                                        documentType: doc.documentType || doc.document_type || 'unknown'
                                    };
                                } catch (error) {
                                    console.error(`[ClaimPage] 환자 ${patient.name} - S3 파일 다운로드 실패: ${doc.fileName}`, error);
                                    return null;
                                }
                            })
                        );

                        // null 값 제거
                        const validFileData = fileData.filter(file => file !== null);

                        // 일렉트론 환경에서만 파일 저장
                        if (window.electronAPI && window.electronAPI.saveCrawlingFiles && validFileData.length > 0) {

                            // claimId를 문자열로 변환
                            const claimIdStr = String(patient.id || `claim_${Date.now()}`);

                            const saveResult = await window.electronAPI.saveCrawlingFiles({
                                files: validFileData,
                                claimId: claimIdStr  // 숫자에서 문자열로 변환
                            });

                            if (saveResult.success) {
                                savedFiles = saveResult.files;
                            } else {
                                console.error(`[ClaimPage] 환자 ${patient.name} - 일렉트론 파일 저장 실패:`, saveResult);
                            }
                        } else {
                            console.warn(`[ClaimPage] 환자 ${patient.name} - 일렉트론 API 없음 또는 유효한 파일 없음`);
                        }
                    } else {
                        console.log(`[ClaimPage] 환자 ${patient.name} - ${nextStep} 단계에 해당하는 파일이 없음 (OrderID: ${patient.orderId})`);
                    }

                    const requestData = this.buildRequestData(patient, taskType);

                    // 파일 경로 추가
                    requestData.files = savedFiles.map(file => file.path || file.name);

                    if (!requestData || !requestData.claimId) {
                        console.error(`[ClaimPage] 환자 ${patient.name}의 requestData 구성 실패:`, requestData);
                        continue;
                    }

                    processedPatients.push({
                        ...patient,
                        requestData: requestData,
                        nextStep: nextStep,
                        taskType: taskType
                    });
                } else {
                    console.log(`[ClaimPage] 환자 ${patient.name} - 이미 완료 상태이므로 건너뜀`);
                }
            }

            console.log(`[ClaimPage] buildProcessedPatients 완료 - 처리 가능한 환자: ${processedPatients.length}명`);
            return processedPatients;
        },

        // 주문서의 선택된 파일 정보 가져오기 (S3 URL 포함)
        async getSelectedFilesForOrder(orderId) {
            try {
                if (!orderId) {
                    console.warn(`[ClaimPage] 주문서 ID가 없습니다.`);
                    return [];
                }

                // 주문서 이미지 정보 가져오기
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getOrderImage/${orderId}`
                );

                if (response.data && response.data.success) {
                    // 모든 문서 반환 (S3 URL 포함) - 자동으로 선택 상태로 처리
                    const documents = (response.data.data.images || []).map(file => {
                        const doc = {
                            id: file.id || file.key,
                            fileName: file.fileName || file.originalname,
                            url: file.url || file.location,  // S3 URL
                            type: file.type || 'image/jpeg',
                            size: file.size || 0,
                            selected: true,  // 자동 선택
                            documentType: file.document_type || file.documentType || 'unknown'
                        };
                        return doc;
                    });

                    // 모든 파일 반환
                    return documents || [];
                } else {
                    console.warn(`[ClaimPage] 주문서 ${orderId} - API 실패:`, response.data);
                    return [];
                }
            } catch (error) {
                console.error(`[ClaimPage] 주문서 ${orderId}의 파일 정보 로드 실패:`, error);
                return [];
            }
        },

        // ================================================================
        // 데이터 관리 관련
        // ================================================================
        // 산소발생기 환자 목록 새로고침
        async loadOxygenPatients() {
            try {
                // companykey를 파라미터로 전달
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/getAllOxygenPatients`, {
                    params: {
                        companykey: this.$globalState.company_key
                    }
                });

                if (response.data && response.data.success) {
                    this.oxygenPatients = response.data.data.map(patient => {
                        const mappedPatient = {
                            id: patient.num,
                            name: patient.name,
                            number: patient.number,
                            phone: patient.phone,
                            orderId: patient.order_id,
                            status: patient.status,
                            claimStatus: this.getClaimStatus(patient),
                            lastClaimDate: patient.oxygen_date || patient.created_at,
                            lastProcessedDate: patient.updated_at,
                            // 산소발생기 관련 데이터
                            oxygenDate: patient.oxygen_date,
                            diseaseName: patient.disease_name,
                            diseaseCode: patient.disease_code,
                            doctorLicense: patient.doctor_number,
                            specialistLicense: patient.specialist_number,
                            startDate: patient.start_date,
                            endDate: patient.end_date,
                            receiptType: patient.receipt_type,
                            issuanceType: patient.issuance_type,
                            homeStartDate: patient.home_start_date,
                            homeEndDate: patient.home_end_date,
                            mobileStartDate: patient.mobile_start_date,
                            mobileEndDate: patient.mobile_end_date,
                            homeOxygenNumber: patient.home_oxygen_number,
                            mobileOxygenNumber: patient.mobile_oxygen_number,
                            homeCount: patient.home_count,
                            mobileCount: patient.mobile_count,
                            taxInvoice: patient.tax_invoice || patient.taxInvoice || '',
                            cashReceipt: patient.cash_receipt || patient.cashReceipt || '',
                            taxInvoiceDate: patient.tax_invoice_date || patient.taxInvoiceDate || '',
                            cashReceiptDate: patient.cash_receipt_date || patient.cashReceiptDate || '',
                            cashReceiptAmount: patient.cash_receipt_amount || patient.cashReceiptAmount || '',
                            taxInvoiceAmount: patient.tax_invoice_amount || patient.taxInvoiceAmount || '',
                            receiptType: patient.receipt_type,
                            // 공인인증서 정보
                            certificateName: patient.certificate_id,
                            certPassword: patient.certificate_pw,
                            // 검사 결과 데이터
                            gasItem1: patient.gas_item1 || 0,
                            gasItem2: patient.gas_item2 || 0,
                            gasItem3: patient.gas_item3 || 0,
                            gasItem4: patient.gas_item4 || 0,
                            gasItem5: patient.gas_item5 || 0,
                            gasItem6: patient.gas_item6 || 0,
                            gasItem7: patient.gas_item7 || 0,
                            oxygenItem1: patient.oxygen_item1 || 0,
                            oxygenItem2: patient.oxygen_item2 || 0,
                            oxygenItem3: patient.oxygen_item3 || 0,
                            oxygenItem4: patient.oxygen_item4 || 0,
                            oxygenItem5: patient.oxygen_item5 || 0,
                            // 문서 목록 추가 - 백엔드에서 documents 필드가 있는 경우
                            documents: patient.documents || []
                        };
                        return mappedPatient;
                    });
                }
            } catch (error) {
                console.error('산소발생기 환자 목록 로드 실패:', error);

                // companykey가 없는 경우 에러 처리
                if (!this.$globalState.company_key) {
                    this.$swal.fire({
                        icon: 'error',
                        title: '회사 정보 없음',
                        text: '회사 정보를 찾을 수 없습니다. 다시 로그인해주세요.',
                        confirmButtonText: '확인'
                    });
                }
            }
        },

        // 환자 상태 업데이트
        async updatePatientStatus(patientId, currentStep) {
            try {
                // 청구등록 성공 시 "청구완료"로 변경
                const statusToUpdate = currentStep === '청구등록' ? '청구완료' : currentStep;

                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/oxygenClaimStatus/${patientId}`,
                    {
                        status: statusToUpdate
                    }
                );

                if (response.data.success) {
                    this.$sentry.logWithComponent('ClaimPage', 'info', '환자 상태 업데이트 성공', {
                        patientId,
                        currentStep: statusToUpdate
                    });

                    // 로컬 상태도 업데이트
                    const patientIndex = this.oxygenPatients.findIndex(p => p.id === patientId);
                    if (patientIndex !== -1) {
                        this.oxygenPatients[patientIndex].status = statusToUpdate;
                    }
                } else {
                    throw new Error(response.data.message || '상태 업데이트 실패');
                }
            } catch (error) {
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'updatePatientStatus',
                    patientId,
                    currentStep
                });
                throw error;
            }
        },

        // 요청 데이터 구성
        buildRequestData(patient, taskType) {
            return {
                claimId: patient.id,
                type: taskType,
                taskType: taskType,
                certificateName: patient.certificateName || this.$globalState.company_certificate_id,
                cert_password: patient.certPassword || this.$globalState.company_certificate_pw,
                client_info: {
                    name: patient.name,
                    resident_num1: patient.number ? patient.number.substring(0, 6) : '',
                    resident_num2: patient.number ? patient.number.substring(6) : '',
                    phone: patient.phone,
                    diseaseName: patient.diseaseName,
                    diseaseCode: patient.diseaseCode,
                    doctorLicense: patient.doctorLicense,
                    specialistLicense: patient.specialistLicense,
                    oxygenDate: patient.oxygenDate ? this.formatDateForAPI(new Date(patient.oxygenDate)) : '',
                    oxygenSdate: patient.startDate ? this.formatDateForAPI(new Date(patient.startDate)) : '',
                    oxygenEdate: patient.endDate ? this.formatDateForAPI(new Date(patient.endDate)) : '',
                    issuanceType: patient.issuanceType,
                    receiptType: patient.receiptType,
                    homeOxygen: patient.receiptType === '가정용' || patient.receiptType === '동시접수',
                    mobileOxygen: patient.receiptType === '휴대용' || patient.receiptType === '동시접수',
                    simultaneousOxygen: patient.receiptType === '동시접수',
                    useHomeStartDate: patient.homeStartDate ? this.formatDateForAPI(new Date(patient.homeStartDate)) : '',
                    useHomeEndDate: patient.homeEndDate ? this.formatDateForAPI(new Date(patient.homeEndDate)) : '',
                    useMobileStartDate: patient.mobileStartDate ? this.formatDateForAPI(new Date(patient.mobileStartDate)) : '',
                    useMobileEndDate: patient.mobileEndDate ? this.formatDateForAPI(new Date(patient.mobileEndDate)) : '',
                    homeBillingCount: patient.homeCount || '1차',
                    mobileBillingCount: patient.mobileCount || '1차',
                    oxygenHomeGenerator: patient.homeOxygenNumber || '',
                    oxygenMobileGenerator: patient.mobileOxygenNumber || '',
                    taxInvoice: patient.tax_invoice || patient.taxInvoice || '',
                    cashReceipt: patient.cash_receipt || patient.cashReceipt || '',
                    taxInvoiceDate: this.formatDateForAPI(patient.tax_invoice_date || patient.taxInvoiceDate) || '',
                    cashReceiptDate: this.formatDateForAPI(patient.cash_receipt_date || patient.cashReceiptDate) || '',
                    cashReceiptAmount: patient.cash_receipt_amount || patient.cashReceiptAmount || '',
                    taxInvoiceAmount: patient.tax_invoice_amount || patient.taxInvoiceAmount || '',
                    bloodGasItem1: patient.gasItem1 === 1,
                    bloodGasItem2: patient.gasItem2 === 1,
                    bloodGasItem3: patient.gasItem3 === 1,
                    bloodGasItem4: patient.gasItem4 === 1,
                    bloodGasItem5: patient.gasItem5 === 1,
                    bloodGasItem6: patient.gasItem6 === 1,
                    bloodGasItem7: patient.gasItem7 === 1,
                    oxygenSaturationItem1: patient.oxygenItem1 === 1,
                    oxygenSaturationItem2: patient.oxygenItem2 === 1,
                    oxygenSaturationItem3: patient.oxygenItem3 === 1,
                    oxygenSaturationItem4: patient.oxygenItem4 === 1,
                    oxygenSaturationItem5: patient.oxygenItem5 === 1
                },
                rental_info: {
                    company_key: this.$globalState.company_key,
                    order_id: patient.orderId
                },
                products: [],
                files: []
            };
        },

        // ================================================================
        // 실패 로그 관련
        // ================================================================
        // 실패 로그 저장
        async saveFailureLog(patient, failureStep, failureReason, errorDetails = null) {
            try {
                // 에러 상세 정보 구성
                const detailedErrorInfo = {
                    claimId: patient.requestData?.claimId || 'N/A',
                    taskType: patient.requestData?.type || 'N/A',
                    certificateName: patient.requestData?.certificateName || 'N/A',
                    residentNumber: patient.number || 'N/A',
                    phone: patient.phone || 'N/A',
                    timestamp: new Date().toISOString(),
                    ...errorDetails
                };

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/automationFailure`,
                    {
                        companykey: this.$globalState.company_key,
                        patientId: patient.id,
                        patientName: patient.name,
                        orderId: patient.orderId,
                        failureStep,
                        failureReason,
                        errorDetails: detailedErrorInfo
                    }
                );

                if (response.data.success) {
                    // 서버에서 받은 실제 DB ID 사용
                    const failureId = response.data.data?.id || response.data.data?.insertId;

                    this.automationFailures.push({
                        num: failureId,  // 실제 DB의 num 값
                        patient_id: patient.id,
                        patient_name: patient.name,
                        order_id: patient.orderId,
                        failure_step: failureStep,
                        failure_reason: failureReason,
                        error_details: detailedErrorInfo,
                        created_at: new Date().toISOString()
                    });

                    this.$sentry.logWithComponent('ClaimPage', 'info', '실패 로그 저장 성공', {
                        patientId: patient.id,
                        failureStep,
                        failureId
                    });
                } else {
                    throw new Error(response.data.message || '실패 로그 저장 실패');
                }
            } catch (error) {
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'saveFailureLog',
                    patientId: patient.id,
                    patientName: patient.name,
                    failureStep
                });
                console.error('실패 로그 저장 실패:', error);

                // 서버 저장 실패 시에도 로컬에 저장
                this.automationFailures.push({
                    num: Date.now(), // 임시 ID
                    patient_id: patient.id,
                    patient_name: patient.name,
                    order_id: patient.orderId,
                    failure_step: failureStep,
                    failure_reason: failureReason,
                    error_details: errorDetails,
                    created_at: new Date().toISOString(),
                    local_only: true // 로컬 저장임을 표시
                });
            }
        },

        // 실패 로그 조회
        async loadAutomationFailures() {
            try {
                this.isLoadingFailures = true;

                const params = {
                    companykey: this.$globalState.company_key
                };

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/automationFailure`, {
                    params: params
                });

                if (response.data.success) {
                    this.automationFailures = response.data.data.failures || [];
                } else {
                    console.error('실패 로그 조회 실패:', response.data.message);
                    this.automationFailures = [];
                }
            } catch (error) {
                console.error('에러 상세:', error.response?.data);
                this.automationFailures = [];
            } finally {
                this.isLoadingFailures = false;
            }
        },

        // 개별 실패 로그 삭제
        async deleteFailureLog(failureId) {
            try {
                if (!failureId || failureId === 'undefined') {
                    throw new Error('유효하지 않은 ID입니다.');
                }

                const response = await this.$axios.delete(`${this.$apiUrls.hanqmedi}/automationFailure/${failureId}`);

                if (response.data && response.data.success) {
                    // 서버 삭제 성공 시 로컬 배열에서 제거
                    this.automationFailures = this.automationFailures.filter(f => f.num !== failureId);

                    this.$sentry.logWithComponent('ClaimPage', 'info', '서버 실패 로그 삭제 성공', {
                        failureId
                    });

                    // 실패한 환자가 모두 삭제되었고 현재 실패 필터가 선택된 상태라면 전체 필터로 변경
                    if (this.automationFailures.length === 0 && this.oxygenFilter === 'failed') {
                        this.oxygenFilter = 'all';
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: '실패 로그가 삭제되었습니다.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data?.message || '서버에서 삭제에 실패했습니다.');
                }
            } catch (error) {
                console.error('삭제 실패:', error);
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'deleteFailureLog',
                    failureId
                });

                this.$swal.fire({
                    icon: 'error',
                    title: '삭제 실패',
                    text: error.response?.data?.message || '실패 로그 삭제에 실패했습니다.',
                    confirmButtonText: '확인'
                });
                throw error;
            }
        },

        // 전체 실패 로그 삭제
        async clearAllFailureLogs() {
            try {
                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/automationFailure`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.success) {
                    this.automationFailures = [];

                    this.$sentry.logWithComponent('ClaimPage', 'info', '전체 실패 로그 삭제 성공');

                    if (this.oxygenFilter === 'failed') {
                        this.oxygenFilter = 'all';
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: '모든 실패 로그가 삭제되었습니다.',
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(response.data.message || '전체 삭제 실패');
                }
            } catch (error) {
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'clearAllFailureLogs'
                });

                this.$swal.fire({
                    icon: 'error',
                    title: '삭제 실패',
                    text: error.response?.data?.message || '전체 실패 로그 삭제에 실패했습니다.',
                    confirmButtonText: '확인'
                });
                throw error;
            }
        },

        // ================================================================
        // 유틸리티 관련
        // ================================================================
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },

        // 다음 단계 결정
        getNextStep(currentStatus) {
            const stepMap = {
                '환자등록': '위임내역등록',
                '위임내역등록': '청구등록',
                '청구등록': '청구완료'
            };

            return stepMap[currentStatus] || '환자등록';
        },

        // 단계를 taskType으로 매핑
        mapStepToTaskType(step) {
            const stepMap = {
                '환자등록': 'check-patient',
                '위임내역등록': 'oxygen-claim-list',
                '청구등록': 'oxygen-claim',
                '청구완료': null
            };
            return stepMap[step];
        },

        // 실패한 환자의 주문서 정보 가져오기
        getFailedPatientOrderInfo(patientId) {
            const patient = this.oxygenPatients.find(p => p.id === patientId);
            console.log('patient:', patient);
            if (patient) {
                return {
                    id: patient.id,
                    name: patient.name,
                    number: patient.number,
                    phone: patient.phone,
                    orderId: patient.orderId,
                    status: patient.status,
                    claimStatus: patient.claimStatus,
                    oxygenDate: patient.oxygenDate,
                    startDate: patient.startDate,
                    endDate: patient.endDate,
                    receiptType: patient.receiptType,
                    homeStartDate: patient.homeStartDate,
                    homeEndDate: patient.homeEndDate,
                    mobileStartDate: patient.mobileStartDate,
                    mobileEndDate: patient.mobileEndDate,
                    homeCount: patient.homeCount,
                    mobileCount: patient.mobileCount,
                    certificateName: patient.certificateName,
                    certPassword: patient.certPassword,
                    taxInvoice: patient.taxInvoice,
                    cashReceipt: patient.cashReceipt,
                    gasItem1: patient.gasItem1,
                    gasItem2: patient.gasItem2,
                    gasItem3: patient.gasItem3,
                    gasItem4: patient.gasItem4,
                    gasItem5: patient.gasItem5,
                    gasItem6: patient.gasItem6,
                    gasItem7: patient.gasItem7,
                    oxygenItem1: patient.oxygenItem1,
                    oxygenItem2: patient.oxygenItem2,
                    oxygenItem3: patient.oxygenItem3,
                    oxygenItem4: patient.oxygenItem4,
                    oxygenItem5: patient.oxygenItem5
                };
            }
            return null;
        },

        // 현재 세션의 실패한 환자 삭제
        deleteCurrentFailure(patientId) {
            const result = this.$swal.fire({
                title: '실패 로그 삭제',
                text: '이 실패 로그를 삭제하시겠습니까?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '삭제',
                cancelButtonText: '취소',
                confirmButtonColor: '#dc2626'
            });

            if (result.isConfirmed) {
                this.failedPatients = this.failedPatients.filter(f => f.id !== patientId);

                // 실패한 환자가 모두 삭제되었고 현재 실패 필터가 선택된 상태라면 전체 필터로 변경
                if (this.failedPatients.length === 0 && this.automationFailures.length === 0 && this.oxygenFilter === 'failed') {
                    this.oxygenFilter = 'all';
                }

                this.$swal.fire({
                    icon: 'success',
                    title: '삭제 완료',
                    text: '실패 로그가 삭제되었습니다.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        },

        // 실패 카드 클릭 이벤트
        handleFailureCardClick() {
            if (this.automationFailures.length === 0) {
                this.$swal.fire({
                    icon: 'info',
                    title: '실패한 환자 없음',
                    text: '실패한 환자가 없습니다.',
                    confirmButtonText: '확인'
                });
                return;
            }
        },

        // 산소발생기 청구 대기 카운트
        getOxygenPendingCount() {
            return this.oxygenPatients.filter(patient => {
                const status = patient.status || patient.claimStatus;

                return status === '환자등록' || status === '위임내역등록' || status === '청구등록';
            }).length;
        },

        // 산소발생기 완료 카운트
        getOxygenCompletedCount() {
            return this.oxygenPatients.filter(p =>
                p.status === '청구완료'
            ).length;
        },

        // 주문서 수정 모달 열기
        openOrderModifyModal(patient, type) {
            if (!patient) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '주문서 정보 없음',
                    text: '해당 환자의 주문서 정보를 찾을 수 없습니다.',
                    confirmButtonText: '확인'
                });
                return;
            }

            // 기존 로직 그대로 사용
            this.selectedPatientForOrder = {
                id: patient.orderId,
                patientId: patient.id,
                orderType: type
            };
            this.showOrderModifyModal = true;
        },

        // 주문서 수정 모달 닫기
        closeOrderModifyModal() {
            this.showOrderModifyModal = false;
            this.selectedPatientForOrder = null;
        },

        // 주문서 수정 완료 후 처리
        handleOrderModifyComplete(updatedOrderData) {
            // 환자 데이터 업데이트
            if (this.selectedPatientForOrder?.orderType === 'oxygen') {
                const index = this.oxygenPatients.findIndex(p => p.id === this.selectedPatientForOrder.id);
                if (index !== -1) {
                    // 업데이트된 주문 데이터를 환자 데이터에 반영
                    this.oxygenPatients.splice(index, 1, {
                        ...this.oxygenPatients[index],
                        ...updatedOrderData
                    });
                }
            } else if (this.selectedPatientForOrder?.orderType === 'diabetes') {
                const index = this.diabetesPatients.findIndex(p => p.id === this.selectedPatientForOrder.id);
                if (index !== -1) {
                    // 업데이트된 주문 데이터를 환자 데이터에 반영
                    this.diabetesPatients.splice(index, 1, {
                        ...this.diabetesPatients[index],
                        ...updatedOrderData
                    });
                }
            }

            // 청구 일정 업데이트
            this.generateUpcomingClaims();
            this.generateCalendarDays();

            this.closeOrderModifyModal();
        },

        // 산소발생기 청구 준비 여부 확인
        isOxygenClaimReady(patient) {
            const today = new Date();
            let claimDate = null;

            if (patient.receiptType === '가정용' && patient.homeEndDate) {
                const endDate = new Date(patient.homeEndDate);
                endDate.setDate(endDate.getDate() + 1);
                claimDate = endDate;
            } else if (patient.receiptType === '휴대용' && patient.mobileEndDate) {
                const endDate = new Date(patient.mobileEndDate);
                endDate.setDate(endDate.getDate() + 1);
                claimDate = endDate;
            } else if (patient.receiptType === '동시접수') {
                // 동시접수는 가정용과 휴대용 중 더 빠른 날짜 사용
                const homeEndDate = patient.homeEndDate ? new Date(patient.homeEndDate) : null;
                const mobileEndDate = patient.mobileEndDate ? new Date(patient.mobileEndDate) : null;

                if (homeEndDate && mobileEndDate) {
                    claimDate = homeEndDate < mobileEndDate ? homeEndDate : mobileEndDate;
                    claimDate.setDate(claimDate.getDate() + 1);
                } else if (homeEndDate) {
                    claimDate = homeEndDate;
                    claimDate.setDate(claimDate.getDate() + 1);
                } else if (mobileEndDate) {
                    claimDate = mobileEndDate;
                    claimDate.setDate(claimDate.getDate() + 1);
                }
            }

            // 청구일이 오늘 이전이면 청구 준비 완료
            return claimDate && claimDate <= today;
        },

        // 산소발생기 통계 카드 클릭 처리
        handleOxygenStatsClick(filter) {
            this.oxygenFilter = filter;

            if (filter === 'failed') {
                // 실패 필터 선택 시 저장된 실패 로그 로드
                if (this.automationFailures.length === 0) {
                    this.loadAutomationFailures();
                }
            }
        },

        // ================================================================
        // 청구 일정 관련 메서드
        // ================================================================
        // 달력 관련 메서드
        generateCalendarDays() {
            const firstDay = new Date(this.currentYear, this.currentMonth, 1);
            const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

            const days = [];
            const today = new Date();

            // 해당 달의 1일부터 말일까지만 생성
            for (let day = 1; day <= lastDay.getDate(); day++) {
                const date = new Date(this.currentYear, this.currentMonth, day);
                const dayEvents = this.getEventsForDate(date);

                days.push({
                    date: date.toISOString().split('T')[0],
                    day: day,
                    isCurrentMonth: true, // 항상 현재 달
                    isToday: date.toDateString() === today.toDateString(),
                    events: dayEvents
                });
            }

            this.calendarDays = days;
        },

        getEventsForDate(date) {
            const dateStr = date.toISOString().split('T')[0];
            let filteredClaims = this.upcomingClaims.filter(claim => claim.date === dateStr);

            if (this.scheduleFilter !== 'all') {
                filteredClaims = filteredClaims.filter(claim => claim.type === this.scheduleFilter);
            }

            return filteredClaims;
        },

        getDayClass(day) {
            const classes = [];

            if (!day.isCurrentMonth) {
                classes.push('other-month');
            }

            if (day.isToday) {
                classes.push('today');
            }

            if (day.events && day.events.length > 0) {
                classes.push('has-events');
            }

            if (this.selectedDay && this.selectedDay.date === day.date) {
                classes.push('selected');
            }

            return classes.join(' ');
        },

        previousMonth() {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
            this.generateCalendarDays();
        },

        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
            this.generateCalendarDays();
        },

        formatSelectedDate(dateStr) {
            const date = new Date(dateStr);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];

            return `${month}월 ${day}일 (${dayOfWeek})`;
        },

        handleScheduleFilterChange() {
            // 달력 다시 생성하여 필터링된 이벤트 표시
            this.generateCalendarDays();
        },

        // 청구 일정 데이터 생성 (당뇨소모성재료 제거)
        generateUpcomingClaims() {
            const claims = [];
            const today = new Date();

            // 산소발생기 환자들의 다음 청구일 계산
            this.oxygenPatients.forEach(patient => {
                // 청구 완료 여부 판단
                const isCompleted = patient.claimStatus === 'completed' || patient.status === 'completed';

                // 처방전 만료일 추가 (endDate)
                if (patient.endDate) {
                    const endDate = new Date(patient.endDate);

                    claims.push({
                        id: `oxygen_expired_${patient.id}`,
                        type: 'expired',
                        date: endDate.toISOString().split('T')[0],
                        title: `${patient.name}`,
                        description: `처방전 기간: ${this.formatDate(patient.startDate)} ~ ${this.formatDate(endDate)}`,
                        patientId: patient.id,
                        isCompleted: isCompleted
                    });
                }

                if (patient.receiptType === '가정용' && patient.homeEndDate) {
                    const endDate = new Date(patient.homeEndDate);
                    const nextClaimDate = new Date(endDate);
                    nextClaimDate.setDate(endDate.getDate() + 1);

                    claims.push({
                        id: `oxygen_home_${patient.id}`,
                        type: 'oxygen',
                        date: nextClaimDate.toISOString().split('T')[0],
                        title: `${patient.name}`,
                        description: `가정용 기간: ${this.formatDate(patient.homeStartDate)} ~ ${this.formatDate(patient.homeEndDate)}`,
                        patientId: patient.id,
                        receiptType: '가정용',
                        isCompleted: isCompleted
                    });
                } else if (patient.receiptType === '휴대용' && patient.mobileEndDate) {
                    const endDate = new Date(patient.mobileEndDate);
                    const nextClaimDate = new Date(endDate);
                    nextClaimDate.setDate(endDate.getDate() + 1);

                    claims.push({
                        id: `oxygen_mobile_${patient.id}`,
                        type: 'oxygen',
                        date: nextClaimDate.toISOString().split('T')[0],
                        title: `${patient.name}`,
                        description: `휴대용 기간: ${this.formatDate(patient.mobileStartDate)} ~ ${this.formatDate(patient.mobileEndDate)}`,
                        patientId: patient.id,
                        receiptType: '휴대용',
                        isCompleted: isCompleted
                    });
                } else if (patient.receiptType === '동시접수') {
                    // 동시접수: 가정용과 휴대용 모두 표시
                    if (patient.homeEndDate) {
                        const homeEndDate = new Date(patient.homeEndDate);
                        const homeNextClaimDate = new Date(homeEndDate);
                        homeNextClaimDate.setDate(homeEndDate.getDate() + 1);

                        claims.push({
                            id: `oxygen_home_${patient.id}`,
                            type: 'oxygen',
                            date: homeNextClaimDate.toISOString().split('T')[0],
                            title: `${patient.name}`,
                            description: `가정용 기간: ${this.formatDate(patient.homeStartDate)} ~ ${this.formatDate(patient.homeEndDate)}`,
                            patientId: patient.id,
                            receiptType: '가정용',
                            isCompleted: isCompleted
                        });
                    }

                    if (patient.mobileEndDate) {
                        const mobileEndDate = new Date(patient.mobileEndDate);
                        const mobileNextClaimDate = new Date(mobileEndDate);
                        mobileNextClaimDate.setDate(mobileEndDate.getDate() + 1);

                        claims.push({
                            id: `oxygen_mobile_${patient.id}`,
                            type: 'oxygen',
                            date: mobileNextClaimDate.toISOString().split('T')[0],
                            title: `${patient.name}`,
                            description: `휴대용 기간: ${this.formatDate(patient.mobileStartDate)} ~ ${this.formatDate(patient.mobileEndDate)}`,
                            patientId: patient.id,
                            receiptType: '휴대용',
                            isCompleted: isCompleted
                        });
                    }
                } else {
                    // receiptType이 없거나 다른 값인 경우 기존 endDate 사용
                    if (patient.endDate) {
                        const endDate = new Date(patient.endDate);
                        const nextClaimDate = new Date(endDate);
                        nextClaimDate.setDate(endDate.getDate() + 1);

                        claims.push({
                            id: `oxygen_${patient.id}`,
                            type: 'oxygen',
                            date: nextClaimDate.toISOString().split('T')[0],
                            title: `${patient.name}`,
                            description: `처방전 기간: ${this.formatDate(patient.startDate)} ~ ${this.formatDate(endDate)}`,
                            patientId: patient.id,
                            receiptType: patient.receiptType || '기본',
                            isCompleted: isCompleted
                        });
                    }
                }
            });

            // 날짜순으로 정렬
            this.upcomingClaims = claims.sort((a, b) => new Date(a.date) - new Date(b.date));
        },

        // 진행 중인 산소발생기 고객 가져오기
        getProcessingOxygenPatient() {
            return this.oxygenPatients.find(p => p.status === 'processing' || p.claimStatus === 'processing');
        },

        // 진행 중인 당뇨소모성재료 고객 가져오기
        getProcessingDiabetesPatient() {
            return this.diabetesPatients.find(p => p.status === 'processing' || p.claimStatus === 'processing');
        },

        // 고객 상태 결정
        getPatientStatus(patient) {
            if (patient.status === 'processing' || patient.claimStatus === 'processing') {
                return 'processing';
            } else if (patient.status === 'error' || patient.claimStatus === 'failed') {
                return 'error';
            } else if (patient.status === 'completed' || patient.claimStatus === 'completed') {
                return 'active';
            }
            return 'active';
        },

        // 청구 상태 결정
        getClaimStatus(patient) {
            if (patient.claimStatus) {
                return patient.claimStatus;
            }

            // 상태에 따른 기본값 설정
            if (patient.status === 'processing') {
                return 'processing';
            } else if (patient.status === 'error') {
                return 'failed';
            } else if (patient.status === 'completed') {
                return 'completed';
            }

            return 'pending';
        },

        // 날짜를 API 형식으로 변환하는 메서드 추가
        formatDateForAPI(date) {
            if (!date) return '';
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        },

        formatPatientNumber(number) {
            if (!number) return '-';
            if (number.length === 13) {
                return number.substring(0, 6) + '-' + number.substring(6, 13);
            }
            return number;
        },

        getClaimCountText(patient) {
            if (!patient) return '-';

            // receipt_type에 따라 해당하는 count 반환
            if (patient.receiptType === '가정용') {
                return patient.homeCount || '-';
            } else if (patient.receiptType === '휴대용') {
                return patient.mobileCount || '-';
            } else {
                // receipt_type이 없거나 다른 값인 경우 home_count 우선, 없으면 mobile_count
                return patient.homeCount || patient.mobileCount || '-';
            }
        },

        getClaimStatusClass(status) {
            const statusClassMap = {
                '청구정보저장': 'claim-status-0',
                '환자등록': 'claim-status-1',
                '위임내역등록': 'claim-status-2',
                '청구등록': 'claim-status-3',
                '청구완료': 'claim-status-completed'
            };
            return statusClassMap[status] || 'claim-status-default';
        },

        getClaimStatusText(status) {
            const statusTextMap = {
                '청구정보저장': '0단계: 청구정보저장',
                '환자등록': '1단계: 환자등록',
                '위임내역등록': '2단계: 위임내역등록',
                '청구등록': '3단계: 청구등록',
                '청구완료': '✓ 청구완료'
            };
            return statusTextMap[status] || status || '미등록';
        },

        selectOxygenPatient(patient) {
            this.selectedOxygenPatient = patient;
        },

        selectDiabetesPatient(patient) {
            this.selectedDiabetesPatient = patient;
        },

        // 통계 업데이트
        updateOxygenStats() {
            this.oxygenStats.totalPatients = this.oxygenPatients.length;
            this.oxygenStats.processedToday = this.oxygenPatients.filter(p => p.lastProcessedDate === this.today).length;
            this.oxygenStats.pending = this.oxygenProcessingList.length;
            this.oxygenStats.failed = this.oxygenPatients.filter(p => p.status === 'error').length;
        },

        updateDiabetesStats() {
            this.diabetesStats.totalPatients = this.diabetesPatients.length;
            this.diabetesStats.processedToday = this.diabetesPatients.filter(p => p.lastProcessedDate === this.today).length;
            this.diabetesStats.pending = this.diabetesProcessingList.length;
            this.diabetesStats.failed = this.diabetesPatients.filter(p => p.status === 'error').length;
        },

        // =========================================
        // 공단 로그인창 관련 메서드
        // =========================================
        async openLongtermLoginModal() {
            try {
                // 이미 로그인 중인지 확인
                if (this.isLoginLoading) {
                    this.$swal.fire({
                        icon: 'warning',
                        title: '로그인 진행 중',
                        text: '이미 공단 로그인을 진행 중입니다. 잠시만 기다려주세요.',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                // 로딩 상태 시작
                this.isLoginLoading = true;
                this.loginMessage = '공단 로그인을 시작합니다...';

                // 로그인 데이터 준비
                const loginData = {
                    type: 'handle-login',
                    certificateName: this.$globalState.company_certificate_id,
                    cert_password: this.$globalState.company_certificate_pw,
                };

                // IPC를 통해 백그라운드에서 크롤링 실행
                const result = await window.electronAPI.startCrawling(loginData);

                if (result.isAlreadyOpen) {
                    // 이미 로그인된 상태일 때만 알림 표시
                    this.$swal.fire({
                        icon: 'info',
                        title: '이미 로그인된 상태',
                        text: result.message || '이미 공단에 로그인된 상태입니다. 기존 창을 사용합니다.',
                        confirmButtonText: '확인'
                    });
                } else if (result.success) {
                    this.loginMessage = '공단 로그인에 성공했습니다!';
                    console.log('공단 로그인 성공:', result.message);
                } else {
                    this.loginMessage = `로그인 실패: ${result.error}`;
                    this.$swal.fire({
                        icon: 'error',
                        title: '로그인 실패',
                        text: result.error || '로그인 중 오류가 발생했습니다.',
                        confirmButtonText: '확인'
                    });
                }

            } catch (error) {
                console.error('공단 로그인 실행 실패:', error);
                this.loginMessage = `로그인 실행 실패: ${error.message}`;
                this.$swal.fire({
                    icon: 'error',
                    title: '로그인 실행 실패',
                    text: error.message,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isLoginLoading = false;
            }
        },

        // =========================================
        // 입출금내역 관련 메서드
        // =========================================

        // YYYYMMDDHHMMSS 형식의 날짜를 Date 객체로 파싱
        parseTransactionDate(dateString) {
            if (!dateString || dateString.length !== 14) return null;

            try {
                const year = parseInt(dateString.substring(0, 4));
                const month = parseInt(dateString.substring(4, 6)) - 1; // 월은 0부터 시작
                const day = parseInt(dateString.substring(6, 8));
                const hour = parseInt(dateString.substring(8, 10));
                const minute = parseInt(dateString.substring(10, 12));
                const second = parseInt(dateString.substring(12, 14));

                return new Date(year, month, day, hour, minute, second);
            } catch (error) {
                console.error('날짜 파싱 오류:', error, dateString);
                return null;
            }
        },

        // 입출금내역 조회
        async loadBankTransactions() {
            try {
                if (!this.$globalState.barobill_id ||
                    typeof this.$globalState.barobill_id === 'number' ||
                    !this.$globalState.barobill_account_no) {

                    try {
                        const barobillInfoResponse = await this.$axios.get(
                            `${this.$apiUrls.hanqmedi}/barobill/getBarobillInfo`,
                            {
                                params: {
                                    companykey: this.$globalState.company_key
                                }
                            }
                        );

                        if (barobillInfoResponse.data.success && barobillInfoResponse.data.data) {
                            const { barobillInfo, accountInfo } = barobillInfoResponse.data.data;

                            // 바로빌 회원 아이디 저장
                            this.$globalState.barobill_id = barobillInfo.bill_id;

                            // 계좌 정보 저장
                            if (accountInfo) {
                                this.$globalState.barobill_account_no = accountInfo.account_no;
                                this.$globalState.barobill_account_start_dt = accountInfo.start_dt;
                                this.$globalState.barobill_account_end_dt = accountInfo.end_dt;
                            } else {
                                console.warn('등록된 바로빌 계좌가 없습니다.');
                                this.$swal.fire({
                                    icon: 'warning',
                                    title: '계좌 정보 없음',
                                    text: '바로빌 계좌를 먼저 등록해주세요.',
                                    confirmButtonText: '확인'
                                });
                                return;
                            }
                        } else {
                            console.error('바로빌 정보 조회 실패:', barobillInfoResponse.data);
                            throw new Error(barobillInfoResponse.data.message || '바로빌 정보를 찾을 수 없습니다.');
                        }
                    } catch (error) {
                        console.error('바로빌 정보 로드 실패:', error);

                        if (error.response) {
                            console.error('에러 응답:', {
                                status: error.response.status,
                                data: error.response.data
                            });
                        }
                        return;
                    }
                }

                // 날짜 형식 결정: 계좌 등록일 기반 또는 사용자 선택 날짜
                let startDate, endDate;

                if (this.$globalState.barobill_account_start_dt) {
                    // 계좌 등록일을 기준으로 시작일 설정
                    const accountStartDate = new Date(this.$globalState.barobill_account_start_dt);
                    const accountStartDateStr = accountStartDate.toISOString().split('T')[0].replace(/-/g, '');

                    // 사용자가 선택한 날짜
                    const userStartDate = this.bankTransactionDateRange.startDate.replace(/-/g, '');
                    const userEndDate = this.bankTransactionDateRange.endDate.replace(/-/g, '');

                    // 계좌 등록일보다 이전 날짜는 조회할 수 없으므로 조정
                    startDate = userStartDate < accountStartDateStr ? accountStartDateStr : userStartDate;
                    endDate = userEndDate;
                } else {
                    // 계좌 등록일 정보가 없으면 사용자 선택 날짜 사용
                    startDate = this.bankTransactionDateRange.startDate.replace(/-/g, '');
                    endDate = this.bankTransactionDateRange.endDate.replace(/-/g, '');
                }

                // 바로빌 API를 통해 입출금내역 조회
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/GetPeriodBankAccountTransLog`,
                    {
                        id: this.$globalState.barobill_id, // 바로빌 회원 아이디 (문자열)
                        bankAccountNum: this.$globalState.barobill_account_no, // DB에서 조회한 계좌번호
                        startDate: startDate, // YYYYMMDD 형식
                        endDate: endDate, // YYYYMMDD 형식
                        transDirection: 1, // 1: 전체 조회
                        countPerPage: 100, // 페이지당 조회 건수
                        currentPage: 1, // 조회할 페이지 번호
                        orderDirection: 2 // 2: DESC (최신순)
                    }
                );

                if (response.data.success) {
                    // 백엔드에서 받은 데이터를 프론트엔드 형식으로 변환
                    const backendData = response.data.data;

                    // transactions 배열이 어디에 있는지 확인
                    let transactions = [];
                    if (backendData.transactions) {
                        transactions = backendData.transactions;
                    } else if (backendData.data && backendData.data.transactions) {
                        transactions = backendData.data.transactions;
                    } else if (Array.isArray(backendData)) {
                        transactions = backendData;
                    } else {
                        transactions = [];
                    }

                    this.bankTransactions = this.transformBackendData(transactions);
                } else {
                    throw new Error(response.data.message || '입출금내역 조회에 실패했습니다.');
                }
            } catch (error) {
                console.error('입출금내역 조회 실패:', error);

                let errorMessage = '입출금내역 조회 중 오류가 발생했습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // 거래메모 편집/작성
        async editTransactionMemo(transaction) {
            try {
                const { value: memo } = await this.$swal.fire({
                    title: '거래메모 작성',
                    input: 'textarea',
                    inputPlaceholder: '거래에 대한 메모를 입력하세요...',
                    inputValue: transaction.transactionMemo || '',
                    inputAttributes: {
                        'aria-label': '거래메모 입력',
                        'maxlength': '200',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '저장',
                    cancelButtonText: '취소',
                    inputValidator: (value) => {
                        if (value && value.length > 200) {
                            return '메모는 200자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (memo !== undefined) { // 취소가 아닌 경우
                    // 로딩 표시
                    this.$swal.fire({
                        title: '메모 저장 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 바로빌 API를 통해 메모 등록
                        const response = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/barobill/RegistBankAccountLogMemo`,
                            {
                                bankAccountNum: transaction.bankAccountNum || '3021727065601',
                                transRefKey: transaction.transRefKey || transaction.transactionRefKey,
                                memo: memo || ''
                            }
                        );

                        if (response.data.success) {
                            // 로컬 데이터 업데이트
                            transaction.transactionMemo = memo;

                            this.$swal.fire({
                                icon: 'success',
                                title: '메모 저장 완료',
                                text: '거래메모가 성공적으로 저장되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '메모 저장에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('메모 저장 실패:', error);

                        let errorMessage = '메모 저장 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '메모 저장 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('메모 편집 중 오류:', error);
            }
        },

        // 백엔드 데이터를 프론트엔드 형식으로 변환
        transformBackendData(backendTransactions) {
            if (!Array.isArray(backendTransactions)) {
                return [];
            }

            const transformed = backendTransactions.map((transaction, index) => {
                // 백엔드 응답 구조를 그대로 활용
                const transformedTransaction = {
                    transactionDate: transaction.TransDT || '', // 거래일시 (YYYYMMDDHHMMSS 형식)
                    withdrawalAmount: parseFloat(transaction.Withdraw || 0), // 출금금액
                    depositAmount: parseFloat(transaction.Deposit || 0), // 입금금액
                    balance: parseFloat(transaction.Balance || 0), // 잔액
                    transRemark1: transaction.TransRemark1 || '', // 거래내용
                    transOffice: transaction.TransOffice || '', // 거래점
                    transType: transaction.TransType || '', // 거래유형
                    transactionMemo: transaction.Memo || '', // 거래메모
                    transRefKey: transaction.TransRefKey || '', // 거래 참조 키 (메모 등록용)
                    bankAccountNum: transaction.BankAccountNum || '3021727065601' // 계좌번호 (메모 등록용)
                };

                return transformedTransaction;
            });

            return transformed;
        },

        // 빠른 날짜 선택 처리
        handleQuickDateSelect() {
            if (!this.quickDateSelect) return;

            const today = new Date();
            let startDate = new Date();

            switch (this.quickDateSelect) {
                case 'today':
                    // 당일: 오늘 하루
                    startDate = new Date(today);
                    break;
                case 'yesterday':
                    // 전일: 어제 하루
                    startDate.setDate(today.getDate() - 1);
                    break;
                case '3days':
                    // 3일: 3일 전부터
                    startDate.setDate(today.getDate() - 3);
                    break;
                case '1week':
                    // 1주: 7일 전부터
                    startDate.setDate(today.getDate() - 7);
                    break;
                case '15days':
                    // 15일: 15일 전부터
                    startDate.setDate(today.getDate() - 15);
                    break;
                case '1month':
                    // 1개월: 30일 전부터
                    startDate.setDate(today.getDate() - 30);
                    break;
                case '3months':
                    // 3개월: 90일 전부터
                    startDate.setDate(today.getDate() - 90);
                    break;
                case '6months':
                    // 6개월: 180일 전부터
                    startDate.setDate(today.getDate() - 180);
                    break;
            }

            // 날짜 범위 설정
            this.bankTransactionDateRange = {
                startDate: startDate.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            // 빠른 선택 후 자동으로 입출금내역 조회
            this.loadBankTransactions();
        },

        // 입출금내역 필터 변경 처리
        handleBankTransactionFilterChange() {
            // 필터 변경 시 로깅
            console.log('입출금내역 필터 변경:', this.bankTransactionFilter);
            console.log('필터링된 결과:', this.filteredBankTransactions.length);
        },

        // 입출금내역 시작일 변경 시 빠른 선택만 초기화
        handleBankTransactionStartDateChange() {
            // 빠른 선택 초기화만 수행
            this.quickDateSelect = '';
        },

        // 입출금내역 종료일 변경 시 빠른 선택만 초기화
        handleBankTransactionEndDateChange() {
            // 빠른 선택 초기화만 수행
            this.quickDateSelect = '';
        },

        // 거래일시 포맷팅 (YYYYMMDDHHMMSS 형식 처리)
        formatTransactionDateTime(dateString) {
            if (!dateString) return '-';

            try {
                // YYYYMMDDHHMMSS 형식 (예: 20250816092209)
                if (dateString.length === 14 && /^\d{14}$/.test(dateString)) {
                    const year = dateString.substring(0, 4);
                    const month = dateString.substring(4, 6);
                    const day = dateString.substring(6, 8);
                    const hour = dateString.substring(8, 10);
                    const minute = dateString.substring(10, 12);
                    const second = dateString.substring(12, 14);

                    return `${year}.${month}.${day} ${hour}:${minute}`;
                }

                // 일반 날짜 형식인 경우
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${year}.${month}.${day} ${hours}:${minutes}`;
            } catch (error) {
                console.error('거래일시 파싱 오류:', error, dateString);
                return dateString;
            }
        },

        // 입출금내역 날짜 범위 초기화
        initializeBankTransactionDateRange() {
            // 기본값: 한 달 전부터 오늘까지 (청구내역과 동일하게)
            const today = new Date();
            const oneMonthAgo = new Date(today);
            oneMonthAgo.setMonth(today.getMonth() - 1); // 한 달 전

            this.bankTransactionDateRange = {
                startDate: oneMonthAgo.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            // 빠른 선택 초기화
            this.quickDateSelect = '';
        },

        // 처리상태 필터 매칭 함수
        matchesStatusFilter(status, filter) {
            if (!status) return false;

            const statusLower = status.toLowerCase();

            switch (filter) {
                case 'pending':
                    return statusLower.includes('대기') ||
                        statusLower.includes('접수') ||
                        statusLower.includes('처리중') ||
                        statusLower.includes('검토중') ||
                        statusLower.includes('보류');
                case 'approved':
                    return statusLower.includes('승인') ||
                        statusLower.includes('완료') ||
                        statusLower.includes('지급') ||
                        statusLower.includes('처리') ||
                        statusLower.includes('지급처리');
                case 'rejected':
                    return statusLower.includes('반려') ||
                        statusLower.includes('제한') ||
                        statusLower.includes('반송') ||
                        statusLower.includes('취소');
                default:
                    return true;
            }
        },

        // 필터 변경 시 처리상태 표시 업데이트
        handleFilterChange() {
            // 각 탭별 데이터 개수 확인
            if (this.currentClaimTab === 'claim-list') {
                console.log('청구위임내역 필터링 결과:', this.filteredCrawledClaims2.length);
            } else if (this.currentClaimTab === 'assistance') {
                console.log('보조기기 필터링 결과:', this.filteredCrawledClaims3.length);
            } else {
                console.log('청구내역 필터링 결과:', this.filteredCrawledClaims.length);
            }
        },

        // 크롤링 결과 처리
        async checkClaim() {
            if (this.$globalState.company_certificate_id === '' || this.$globalState.company_certificate_pw === '') {
                this.$swal.fire({
                    icon: 'info',
                    title: '공단 청구조회 불가능',
                    html: '공인인증서 정보가 설정되지 않았습니다. <br>관리자에게 문의하세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            if (this.isCrawling) {
                console.log('[ClaimPage] 로컬에서 크롤링 진행 중');
                this.$swal.fire({
                    title: '크롤링 진행 중',
                    text: '이미 청구내역 조회가 진행 중입니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            if (!this.validateDateRange()) {
                return;
            }

            try {
                this.isCrawling = true;

                const result = await window.electronAPI.startCrawling({
                    type: 'check-claim',
                    certificateName: this.$globalState.company_certificate_id,
                    cert_password: this.$globalState.company_certificate_pw,
                    startDate: this.claimDateRange.startDate,
                    endDate: this.claimDateRange.endDate,
                    claimId: `check_claim_${Date.now()}`
                });

                if (result.success) {
                    this.crawledClaimData = result.data.claimData || [];
                    this.crawledClaimData2 = result.data.claimData2 || [];
                    this.crawledClaimData3 = result.data.claimData3 || [];
                    this.totalCount = this.crawledClaimData.length + this.crawledClaimData2.length + this.crawledClaimData3.length;

                    try {
                        await this.loadBankTransactions();
                    } catch (bankError) {
                        console.warn('입출금내역 조회 실패:', bankError);
                    }

                    try {
                        const saveResponse = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/saveCrawledClaims`,
                            {
                                companykey: this.$globalState.company_key,
                                claimData: {
                                    claimData: this.crawledClaimData,
                                    claimData2: this.crawledClaimData2,
                                    claimData3: this.crawledClaimData3
                                }
                            }
                        );

                        if (saveResponse.data.success) {
                            this.$sentry.logWithComponent('ClaimPage', 'info', '청구내역 조회 및 저장 성공', {
                                totalCount: this.totalCount
                            });

                            this.$swal.fire({
                                icon: 'success',
                                title: '조회 완료',
                                html: `총 ${this.totalCount}건의 청구내역을 조회했습니다.`,
                                showConfirmButton: true,
                                confirmButtonText: '확인'
                            });
                        } else {
                            throw new Error(saveResponse.data.message || '데이터 저장에 실패했습니다.');
                        }
                    } catch (saveError) {
                        this.$sentry.errorWithComponent('ClaimPage', saveError, {
                            action: 'saveCrawledClaims',
                            totalCount: this.totalCount
                        });

                        console.error('데이터 저장 실패:', saveError);
                        this.$swal.fire({
                            icon: 'warning',
                            title: '조회 완료, 저장 실패',
                            html: `청구내역은 조회되었지만 저장에 실패했습니다.<br><br>조회된 데이터: ${this.totalCount}건`,
                            showConfirmButton: true,
                            confirmButtonText: '확인'
                        });
                    }
                } else {
                    throw new Error(result.error || '청구내역 조회에 실패했습니다.');
                }

            } catch (error) {
                this.$sentry.errorWithComponent('ClaimPage', error, {
                    action: 'checkClaim',
                    startDate: this.claimDateRange.startDate,
                    endDate: this.claimDateRange.endDate
                });

                console.error('청구내역 조회 실패:', error);
                this.crawledClaimData = [];
                this.crawledClaimData2 = [];
                this.crawledClaimData3 = [];

                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: error.message || '청구내역 조회 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            } finally {
                this.isCrawling = false;
            }
        },

        // 저장된 청구 내역 불러오기
        async loadSavedClaims() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getSavedClaims`,
                    {
                        params: {
                            companykey: this.$globalState.company_key,
                            status: this.claimsFilter,
                            startDate: this.claimDateRange.startDate,
                            endDate: this.claimDateRange.endDate
                        }
                    }
                );

                if (response.data.success) {
                    const savedData = response.data.data.claims;
                    const counts = response.data.data.counts;
                    const total = response.data.data.total;

                    // 각 테이블별로 데이터를 별도로 설정하고 날짜 기준으로 정렬
                    this.crawledClaimData2 = this.sortByDate(savedData.delegation || [], 'claim_date');
                    // 산소와 당뇨 데이터를 모두 포함하고 날짜 기준으로 정렬
                    const combinedData = [...(savedData.oxygen || []), ...(savedData.diabetes || [])];
                    this.crawledClaimData = this.sortByDate(combinedData, 'claim_date');
                    this.crawledClaimData3 = this.sortByDate(savedData.assistance || [], 'claim_date');

                    this.totalCount = total;

                    // 입출금내역 날짜 범위를 청구내역과 동일하게 설정
                    this.bankTransactionDateRange = {
                        startDate: this.claimDateRange.startDate,
                        endDate: this.claimDateRange.endDate
                    };

                    // 입출금내역도 함께 불러오기
                    try {
                        await this.loadBankTransactions();
                    } catch (bankError) {
                        console.error('저장된 청구내역 불러오기 - 입출금내역 불러오기 실패:', bankError);

                        // 입출금내역 조회 실패 시 사용자에게 알림
                        this.$swal.fire({
                            icon: 'warning',
                            title: '입출금내역 조회 실패',
                            html: `청구내역은 불러왔지만 입출금내역 조회에 실패했습니다.<br><br>
                           거래일시와 거래기록사항이 표시되지 않을 수 있습니다.<br>
                           오류: ${bankError.message || '알 수 없는 오류'}`,
                            confirmButtonText: '확인'
                        });
                    }

                    // 현재 선택된 탭에 따라 데이터 표시
                    this.updateCurrentTabData();
                } else {
                    throw new Error(response.data.message || '저장된 청구내역 불러오기에 실패했습니다.');
                }
            } catch (error) {
                console.error('저장된 청구내역 불러오기 실패:', error);
            }
        },

        // 날짜 기준으로 정렬하는 함수 추가
        sortByDate(data, dateField) {
            if (!Array.isArray(data) || data.length === 0) return data;

            return data.sort((a, b) => {
                const dateA = new Date(a[dateField] || a.created_at || 0);
                const dateB = new Date(b[dateField] || b.created_at || 0);

                // 최신 날짜가 위로 오도록 내림차순 정렬
                return dateB - dateA;
            });
        },

        // 현재 탭에 맞는 데이터 업데이트
        updateCurrentTabData() {
            // 각 탭별 데이터 개수 로깅
            if (this.currentClaimTab === 'claim-list') {
                console.log('청구위임내역 필터링 결과:', this.filteredCrawledClaims2.length);
            } else if (this.currentClaimTab === 'assistance') {
                console.log('보조기기 필터링 결과:', this.filteredCrawledClaims3.length);
            } else {
                console.log('청구내역 필터링 결과:', this.filteredCrawledClaims.length);
            }
        },

        // 지급 거래의 거래일시 가져오기
        getPaymentTransactionDate(claim) {
            const matchingTransaction = this.getMatchingTransaction(claim);
            if (matchingTransaction) {
                return this.formatTransactionDateTime(matchingTransaction.transactionDate);
            }
            return '-';
        },

        // 지급 거래의 거래기록사항 가져오기
        getPaymentTransactionRemark(claim) {
            const matchingTransaction = this.getMatchingTransaction(claim);
            if (matchingTransaction) {
                return matchingTransaction.transRemark1 || '-';
            }
            return '-';
        },

        // 매칭되는 거래 찾기 (수정된 버전)
        getMatchingTransaction(claim) {
            if (!claim || !this.bankTransactions || this.bankTransactions.length === 0) {
                return null;
            }

            // 수진자 주민번호 앞자리 추출 (6자리)
            const patientNumber = claim.patient_number || claim.수진자주민번호 || '';
            if (!patientNumber || patientNumber.length < 6) {
                return null;
            }

            const patientNumberPrefix = patientNumber.substring(0, 6);

            // 청구 접수일자 추출 및 변환
            const claimDate = this.getClaimDate(claim);
            if (!claimDate) {
                return null;
            }

            // 입출금내역에서 해당 주민번호 앞자리가 포함되고 접수일자 이후의 거래 찾기
            const matchingTransaction = this.bankTransactions.find(transaction => {
                const transRemark = transaction.transRemark1 || '';
                const isNumberMatch = transRemark.includes(patientNumberPrefix);

                if (!isNumberMatch) return false;

                // 거래일시가 접수일자 이후인지 확인
                const transactionDate = this.parseTransactionDate(transaction.transactionDate);
                if (!transactionDate) return false;

                const isDateMatch = transactionDate >= claimDate;

                return isDateMatch;
            });

            return matchingTransaction;
        },

        // 청구 접수일자 추출 및 Date 객체로 변환하는 헬퍼 메서드 (수정된 버전)
        getClaimDate(claim) {
            const claimDateStr = claim.claim_date || claim.접수입력일자 || '';
            if (!claimDateStr) {
                return null;
            }

            try {
                let date;

                // ISO 형식 (2025-10-20T15:00:00.000Z)인 경우
                if (claimDateStr.includes('T') || claimDateStr.includes('Z')) {
                    date = new Date(claimDateStr);
                }
                // "2025.08.04" 형식인 경우
                else if (claimDateStr.includes('.')) {
                    const formattedDate = claimDateStr.replace(/\./g, '-');
                    date = new Date(formattedDate);
                }
                // 일반 날짜 형식인 경우
                else {
                    date = new Date(claimDateStr);
                }

                // 유효한 날짜인지 확인
                if (isNaN(date.getTime())) {
                    console.error('접수일자 파싱 실패 - Invalid Date:', claimDateStr);
                    return null;
                }
                return date;

            } catch (error) {
                console.error('접수일자 파싱 오류:', error, claimDateStr);
                return null;
            }
        },

        // 청구 내역 관련 유틸리티 메서드 (수정된 버전)
        getPaymentStatus(claim) {
            if (!claim || !this.bankTransactions || this.bankTransactions.length === 0) {
                return '미지급';
            }

            // 수진자 주민번호 앞자리 추출 (6자리)
            const patientNumber = claim.patient_number || claim.수진자주민번호 || '';
            if (!patientNumber || patientNumber.length < 6) {
                return '미지급';
            }

            const patientNumberPrefix = patientNumber.substring(0, 6);

            // 청구 접수일자 추출 및 변환
            const claimDate = this.getClaimDate(claim);
            if (!claimDate) {
                return '미지급';
            }

            // 입출금내역에서 해당 주민번호 앞자리가 포함되고 접수일자 이후의 거래 찾기
            const matchingTransaction = this.bankTransactions.find(transaction => {
                const transRemark = transaction.transRemark1 || '';
                const isNumberMatch = transRemark.includes(patientNumberPrefix);

                if (!isNumberMatch) return false;

                // 거래일시가 접수일자 이후인지 확인
                const transactionDate = this.parseTransactionDate(transaction.transactionDate);
                if (!transactionDate) return false;

                return transactionDate >= claimDate;
            });

            return matchingTransaction ? '지급' : '미지급';
        },

        getPaymentStatusClass(status) {
            return status === '지급' ? 'status-completed' : 'status-pending';
        },

        getProcessStatusClass(status) {
            if (!status) return 'status-default';

            // 산소발생기 청구내역
            if (status.includes('지급처리')) return 'status-completed';
            if (status.includes('승인')) return 'status-completed';
            if (status.includes('반려')) return 'status-rejected';
            if (status.includes('지급불가')) return 'status-rejected';
            if (status.includes('반려(재신청완료)')) return 'status-processing';
            if (status.includes('급여제한')) return 'status-rejected';

            // 보조기기 청구내역
            if (status.includes('지급처리')) return 'status-completed';
            if (status.includes('급여제한')) return 'status-rejected';
            if (status.includes('지급의뢰')) return 'status-processing';

            return 'status-default';
        },

        getBenefitTypeClass(benefitType) {
            if (!benefitType) return 'type-default';

            if (benefitType.includes('산소')) return 'type-oxygen';
            if (benefitType.includes('당뇨')) return 'type-diabetes';
            if (benefitType.includes('보조')) return 'type-assistance';

            return 'type-default';
        },

        formatClaimDate(dateString) {
            if (!dateString || dateString.trim() === '') return '-';

            try {
                let date;

                // ISO 형식 (2025-07-30T15:00:00.000Z) 또는 타임스탬프 형식인 경우
                if (dateString.includes('T') || dateString.includes('Z') || dateString.includes(':')) {
                    date = new Date(dateString);
                }
                // "2025.08.04" 형식인 경우
                else if (dateString.includes('.')) {
                    const formattedDate = dateString.replace(/\./g, '-');
                    date = new Date(formattedDate);
                }
                // 일반 날짜 형식 (2025-08-25)인 경우
                else {
                    date = new Date(dateString);
                }

                if (isNaN(date.getTime())) {
                    return dateString; // 파싱 실패 시 원본 반환
                }

                // 한국 시간대로 변환하여 날짜만 반환 (시분초 제거)
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');

                return `${year}. ${month}. ${day}.`;

            } catch (error) {
                console.error('날짜 파싱 오류:', error, dateString);
                return dateString; // 에러 발생 시 원본 반환
            }
        },

        formatClaimAmount(amount) {
            // null, undefined, 빈 문자열 체크
            if (amount === null || amount === undefined || amount === '') {
                return '-';
            }

            // 숫자인 경우
            if (typeof amount === 'number') {
                return new Intl.NumberFormat('ko-KR').format(amount) + '원';
            }

            // 문자열인 경우
            if (typeof amount === 'string') {
                const trimmedAmount = amount.trim();
                if (trimmedAmount === '') return '-';

                // 숫자만 추출하여 천 단위 콤마 추가
                const numericAmount = trimmedAmount.replace(/[^\d]/g, '');
                if (numericAmount === '') return amount;

                return new Intl.NumberFormat('ko-KR').format(parseInt(numericAmount)) + '원';
            }

            // 기타 타입인 경우
            return '-';
        },

        // =========================================
        // 날짜 범위 초기화
        // =========================================
        initializeDateRange() {
            const today = new Date();
            const oneMonthAgo = new Date(today);
            oneMonthAgo.setMonth(today.getMonth() - 1); // 한 달 전
            oneMonthAgo.setDate(oneMonthAgo.getDate() + 1); // +1일

            this.claimDateRange = {
                startDate: oneMonthAgo.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]  // 오늘 날짜로 설정
            };
        },

        // 시작일 변경 시 종료일 자동 조정
        handleStartDateChange() {
            if (this.claimDateRange.startDate) {
                const startDate = new Date(this.claimDateRange.startDate);
                const endDate = new Date(startDate);

                // 시작일로부터 1달 뒤로 설정 (최대 31일)
                endDate.setDate(startDate.getDate() + 30);

                // 종료일이 오늘을 넘어가지 않도록 제한
                const today = new Date();
                if (endDate > today) {
                    endDate.setTime(today.getTime());
                }

                this.claimDateRange.endDate = endDate.toISOString().split('T')[0];
            }
        },

        // 종료일 변경 시 시작일 자동 조정
        handleEndDateChange() {
            if (this.claimDateRange.endDate) {
                const endDate = new Date(this.claimDateRange.endDate);
                const startDate = new Date(endDate);

                // 종료일로부터 1달 앞으로 설정 (최대 31일)
                startDate.setDate(endDate.getDate() - 30);

                // 시작일이 너무 과거로 가지 않도록 제한 (예: 1년 전)
                const minDate = new Date();
                minDate.setFullYear(minDate.getFullYear() - 1);
                if (startDate < minDate) {
                    startDate.setTime(minDate.getTime());
                }

                this.claimDateRange.startDate = startDate.toISOString().split('T')[0];
            }
        },

        // 날짜 범위 유효성 검사 및 경고 메시지
        validateDateRange() {
            if (!this.isDateRangeValid) {
                if (this.claimDateRange.startDate && this.claimDateRange.endDate) {
                    const startDate = new Date(this.claimDateRange.startDate);
                    const endDate = new Date(this.claimDateRange.endDate);
                    const diffTime = endDate.getTime() - startDate.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays > 31) {
                        this.$swal.fire({
                            title: '날짜 범위 초과',
                            text: '조회 기간은 최대 1달(31일)까지 가능합니다.',
                            icon: 'warning',
                            confirmButtonText: '확인'
                        });
                        return false;
                    }
                }
            }
            return true;
        },

        // =========================================
        // 통합 알림 시스템
        // =========================================
        showNotification(type, title, message, options = {}) {
            const defaultOptions = {
                toast: true,
                position: 'top-end',
                timer: 5000,
                timerProgressBar: true,
                showConfirmButton: false
            };

            const notificationOptions = { ...defaultOptions, ...options };

            this.$swal.fire({
                icon: type,
                title: title,
                text: message,
                ...notificationOptions
            });

            // 글로벌 메시지 시스템에 추가
            if (this.globalMessageSystem) {
                const newMessage = {
                    id: Date.now(),
                    type: type,
                    title: title,
                    text: message,
                    timestamp: new Date(),
                    isRead: false
                };

                this.globalMessageSystem.addMessage(newMessage);
            }
        },

        // 특화된 알림 메서드들
        showSuccessNotification(title, message, options = {}) {
            this.showNotification('success', title, message, options);
        },

        showErrorNotification(title, message, options = {}) {
            this.showNotification('error', title, message, {
                timer: 8000,
                ...options
            });
        },

        showWarningNotification(title, message, options = {}) {
            this.showNotification('warning', title, message, options);
        },

        showInfoNotification(title, message, options = {}) {
            this.showNotification('info', title, message, options);
        },

        // 환자별 알림 메서드들
        showPatientClaimStartNotification(patientName, step) {
            this.showInfoNotification(`${patientName}님의 ${step}이 시작되었습니다`, '');
        },

        showPatientClaimCompleteNotification(patientName, step) {
            this.showSuccessNotification(`${patientName}님의 ${step}이 완료되었습니다`, '');
        },

        showPatientClaimFailureNotification(patientName, step) {
            this.showErrorNotification(`${patientName}님의 ${step}이 실패했습니다`);
        },

        // 자동화 알림 메서드들
        showAutomationStartNotification() {
            this.showInfoNotification('청구 자동화가 시작되었습니다', '');
        },

        showAutomationCompleteNotification() {
            this.showSuccessNotification('청구 자동화가 완료되었습니다', '');
        },

        showAutomationStopNotification() {
            this.showWarningNotification('청구 자동화가 중지되었습니다', '');
        },

        // =========================================
        // 모달 관련 메서드
        // =========================================
        handleRegistrationSuccess(formData) {
            this.showRegistrationModal = false;
        },

        viewClaimDetail(claim) {
            alert(`청구 상세 정보:\n청구ID: ${claim.id}\n금액: ${this.formatPrice(claim.amount)}원`);
        },

        editClaim(claim) {
            alert(`청구 수정: ${claim.id}`);
        },

        deleteClaim(claim) {
            if (confirm('정말 삭제하시겠습니까?')) {
                this.claims = this.claims.filter(c => c.id !== claim.id);
            }
        },

        formatPhoneNumber(phone) {
            if (!phone) return '-';
            return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('ko-KR');
        },

        formatPrice(price) {
            return new Intl.NumberFormat('ko-KR').format(price || 0);
        },

        getStatusText(status) {
            const statusMap = {
                '일반': '일반',
                '감경': '감경',
                '의료급여': '의료급여',
                '기초': '기초'
            };
            return statusMap[status] || '일반';
        },

        // 날짜 선택
        selectDay(day, event) {
            // 일정이 없는 날 클릭 시 선택/툴팁 모두 해제
            if (!day.events || day.events.length === 0) {
                this.selectedDay = null;
                this.tooltipDay = null;
                this.isTooltipPinned = false;
                return;
            }

            // 같은 날을 다시 클릭하면 토글(해제)
            if (this.selectedDay && this.selectedDay.date === day.date) {
                this.selectedDay = null;
                this.tooltipDay = null;
                this.isTooltipPinned = false;
                return;
            }

            // 새로 선택
            this.selectedDay = day;
            this.tooltipDay = day;
            this.isTooltipPinned = true;
            this.calculateTooltipPosition(event);
        },

        // 툴팁 위치 계산
        calculateTooltipPosition(event) {
            if (!event || !event.currentTarget) {
                this.tooltipPosition = { x: 10, y: 80 };
                return;
            }

            const rect = event.currentTarget.getBoundingClientRect();
            const tooltipWidth = 300;
            const tooltipHeight = 200;
            const padding = 10;

            let x = 10;
            let y = rect.height;

            const parentRect = event.currentTarget.closest('.calendar-section, [class*="CalendarSection"]');
            const containerRect = parentRect ? parentRect.getBoundingClientRect() : document.body.getBoundingClientRect();

            if (rect.left + x + tooltipWidth > containerRect.right - padding) {
                x = rect.width - tooltipWidth - 10;
                if (x < -rect.left + padding) {
                    x = -rect.left + padding;
                }
            }

            if (rect.bottom + tooltipHeight > containerRect.bottom - padding) {
                y = -tooltipHeight - 5;
            }

            this.tooltipPosition = { x, y };
        },
    },

    mounted() {
        this.initializeDateRange(); // 청구내역 날짜 범위 초기화
        this.initializeBankTransactionDateRange(); // 입출금내역 날짜 범위 초기화

        // 입출금내역을 먼저 로드한 후 청구내역 로드
        this.loadBankTransactions().then(() => {
            this.loadSavedClaims(); // 최근 청구내역 불러오기
        }).catch((bankError) => {
            console.warn('입출금내역 로드 실패, 청구내역만 로드:', bankError);
            this.loadSavedClaims(); // 입출금내역 실패해도 청구내역은 로드
        });

        this.loadOxygenPatients(); // 산소발생기 고객 목록 조회
        this.generateCalendarDays(); // 달력 초기화
        this.generateUpcomingClaims(); // 청구 일정 데이터 생성
        this.loadAutomationFailures(); // 자동화 실패 로그 로드

        // this.loadAutomationSettings();

        this.crawledClaimData = [];
        this.crawledClaimData2 = [];
        this.crawledClaimData3 = [];
        this.bankTransactions = [];

        // 산소발생기 환자 목록 새로고침 이벤트 리스너 등록
        window.addEventListener('refresh-oxygen-patients', this.handleRefreshOxygenPatients);
    },

    beforeUnmount() {
        // 타이머 정리
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        // 이벤트 리스너 제거
        window.removeEventListener('refresh-oxygen-patients', this.handleRefreshOxygenPatients);
    },

    watch: {
        // 청구내역 탭 변경 시 데이터 로딩
        currentClaimTab: {
            handler(newTab) {
                this.updateCurrentTabData();
            },
            immediate: false
        },

        // 환자 데이터 변경 시 청구 일정 업데이트
        oxygenPatients: {
            handler() {
                this.generateUpcomingClaims();
                this.generateCalendarDays();
            },
            deep: true
        }
    },
};
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>